import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBackToDashboard } from "../../utils/navigationUtils";
import {
  ArrowLeft,
  User,
  FileText,
  MessageSquare,
  Activity,
  Shield,
  Eye,
  Download,
  X,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Building,
  Stethoscope,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  DollarSign,
  CreditCard,
  Calculator,
  Paperclip,
  Target,
  TrendingUp,
  TrendingDown,
  Percent,
  Award,
  AlertCircle,
  Info,
  Edit,
  Save,
  RefreshCw,
  Banknote,
  Receipt,
  FileCheck,
  Zap,
  Star,
  Users,
  Home,
  Briefcase,
  BookOpen,
  PieChart,
  BarChart3,
  Loader,
  Upload,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  FileCheck as FileCheckIcon,
  ExternalLink,
  Search,
  Filter,
  AlertOctagon,
  TrendingUp as TrendUp,
  BarChart,
  PieChart as PieChartIcon,
  Wallet,
  CreditCard as CreditCardIcon,
  BadgeCheck,
  XSquare,
  Clock as ClockIcon,
  FileX,
  UserCheck,
  Building as Bank,
  Globe,
  Key,
  ShieldCheck,
  History,
  Database,
  FileSpreadsheet,
  CircleDollarSign,
  TrendingDown as TrendDown,
  Fingerprint,
  Lock,
  Plus,
  Trash2,
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import ApplicationRightPanel from "../../components/policy/ApplicationRightPanel";
import {
  getPatientApplication,
  subscribeToApplicationUpdates,
  getApplicationDiscussions,
  sendDiscussionMessage,
  markMessagesAsRead,
  updateReviewActivity
} from "../../services/doctorService";

import { checkApplicationDisputeStatus, getApplicationDisputes } from "../../services/disputeService";
import { getApplicationSanctions } from "../../services/applicationService";
import { 
  raiseDocumentQuery, 
  getDocumentQueries 
} from "../../services/documentQueryService";

import {
  completeFinancialVerification,
  fetchCreditScore,
  verifyDocumentAuthenticity,
  calculateFinancialRisk,
  getFinancialHistory,
  saveFinancialComments,
  getFinancialComments,
  updateFinancialStatus
} from "../../services/financialservice";
import DisputeManager from "../../components/common/DisputeManager";
import FinancialReviewForm from "./FinancialReviewForm";
import DiscussionComponent from "../../component/application/DiscussionComponent";
import FALPreviewModal from "../../components/common/FALPreviewModal";
import GlobalSearchBar from "../../components/common/GlobalSearchBar";

// Utility function to remove File objects from data before saving to Firestore
const cleanDataForFirestore = (data) => {
  if (data === null || data === undefined) {
    return data;
  }
  
  if (typeof data === 'object' && data.constructor === File) {
    // Skip File objects entirely
    return null;
  }
  
  if (Array.isArray(data)) {
    return data.map(item => cleanDataForFirestore(item)).filter(item => item !== null);
  }
  
  if (typeof data === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(data)) {
      const cleanedValue = cleanDataForFirestore(value);
      if (cleanedValue !== null) {
        cleaned[key] = cleanedValue;
      }
    }
    return cleaned;
  }
  
  return data;
};

// Helper: determine if the application has progressed beyond financial verification
function isPostFinanceLocked(app) {
  if (!app) return false;
  const top = (app.status || '').toLowerCase();
  const wf = app.workflow || {};
  const finStatus = (wf.financialVerification?.status || '').toLowerCase();
  const disbursalStatus = (wf.disbursal?.status || '').toLowerCase();
  const recoveryStatus = (wf.recovery?.status || '').toLowerCase();
  // Any of these imply we must not restart financial review
  const topLevelLocked = [
    'financial_approved',
    'financial_rejected',
    'pending_financial_clarification',
    'settlement_approved',
    'ready_for_disbursal',
    'amount_disbursed',
    'case_closed',
    'dispute_pending',
    'approved' // full approval synonym in some flows
  ];
  if (topLevelLocked.includes(top)) return true;
  // If finance shows approved, lock as well
  if (finStatus === 'approved' || finStatus === 'rejected' || finStatus === 'pending_info') return true;
  // If disbursal/recovery phases started, also lock
  if (disbursalStatus && disbursalStatus !== 'pending') return true; // disbursal actively managed
  if (recoveryStatus) return true;
  // If discharge settlement got approved, don't restart
  const dsStatus = (wf.dischargeSettlement?.status || '').toLowerCase();
  if (dsStatus === 'approved') return true;
  return false;
}

const FinancialApplicationReview = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { userData: user } = useAuth();
  const goBackToDashboard = useBackToDashboard();
  const timerRef = useRef(null);
  const activityRef = useRef(null);
  const messagesEndRef = useRef(null);

  const [application, setApplication] = useState(null);
  // Sticky effective approval to prevent UI from flipping back to not-approved on partial snapshots
  const [financialApprovedEffective, setFinancialApprovedEffective] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  // Timer state
  const [reviewStartTime, setReviewStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);

  // Review state
  const [activeTab, setActiveTab] = useState("application");
  const [reviewDecision, setReviewDecision] = useState("");
  const [reviewComments, setReviewComments] = useState("");
  // Per-tab scroll memory and helpers
  const tabScrollRef = useRef({});
  const rememberScroll = () => {
    try { return window.scrollY || 0; } catch (e) { return 0; }
  };
  const restoreScroll = (y) => {
    try {
      requestAnimationFrame(() => {
        window.scrollTo(0, y || 0);
        setTimeout(() => window.scrollTo(0, y || 0), 0);
      });
    } catch (e) {}
  };
  const changeTab = (nextId) => {
    tabScrollRef.current[activeTab] = rememberScroll();
    setActiveTab(nextId);
    const saved = tabScrollRef.current[nextId] || 0;
    restoreScroll(saved);
  };

  // Enhanced Financial Assessment State - comprehensive form matching all required fields
  const [financialAssessment, setFinancialAssessment] = useState({
    // Personal Details
    isPatientBorrowerSame: false,
    patientName: "",
    borrowerName: "",
    borrowerDateOfBirth: "",
    emailId: "",
    contactNumber: "",
    alternateContactNumber: "",
    relationToBorrower: "",
    occupation: "",
    companyName: "",
    aadharNumber: "",
    panNumber: "",
    patientGender: "",
    borrowerGender: "",
    borrowerMaritalStatus: "",

    // Address Details
    isCurrentPermanentSame: false,
    permanentAddress: {
      pincode: "",
      state: "",
      district: "",
      city: "",
      address: ""
    },
    currentAddress: {
      pincode: "",
      state: "",
      district: "",
      city: "",
      address: ""
    },

    // Reference Details
    reference: {
      name: "",
      contactNumber: "",
      relationship: "",
      emailId: ""
    },

    // Hospital Information
    hospital: {
      name: "",
      bankAccountNumber: "",
      bankName: "",
      beneficiaryName: "",
      branchName: "",
      ifscCode: "",
      aggregatorName: "Abhiraksha"
    },

    // Treatment Details
    treatmentDetails: "",

    // Claim Processing Form
    claimProcessing: {
      insuranceCompany: "",
      isInHouseTPA: false,
      tpa: "",
      uhidNumber: "",
      policyNumber: "",
      sumInsuredAmount: 0,
      isCorporatePolicy: false,
      policyStartDate: "",
      policyEndDate: ""
    },

    // Bank Details (Cancel Cheque Details)
    borrowerBank: {
      bankName: "",
      branchName: "",
      accountNumber: "",
      ifscCode: ""
    },

    // Loan Details
    loanDetails: {
      totalMonthlyIncome: 0,
      schemeMonths: 12,
      interestSubventionPercent: 0,
      processingFeePercent: 0
    },

    // Credit Information
    creditScore: null,
    creditRating: "",
    creditReportDate: "",
    creditBureau: "CIBIL", // CIBIL, Experian, Equifax, CRIF

    // Enhanced Credit Additional Information
    creditAdditionalInfo: {
      customCreditScore: '',
      creditBureauName: 'CIBIL',
      creditReportDate: '',
      previousCreditHistory: '',
      defaultHistory: '',
      additionalNotes: [],
      customFields: []
    },

    // KYC Verification Status
    aadharVerified: false,
    panVerified: false,
    bankAccountVerified: false,
    addressVerified: false,
    incomeVerified: false,
    employmentVerified: false,
    documentsAuthentic: false,

    // Internal Loan Tracking
    loanId: "",
    lenderName: "",
    loanType: "medical", // medical, personal, emergency
    eligibleAmount: 0,
    recommendedAmount: 0,
    approvedAmount: 0,

    // Financial Terms
    interestRate: 0,
    processingFee: 0,
    processingFeeType: "amount", // amount, percentage
    tenure: 12, // months
    emi: 0,
    disbursalMode: "bank_transfer",

    // Risk Assessment
    riskAssessment: "", // keeping for backward compatibility
    riskCategory: "", // low, medium, high, very_high
    riskScore: 0,
    riskFactors: [],
    mitigationMeasures: [],

    // Additional Financial Data
    monthlyIncome: 0,
    existingEMI: 0,
    debtToIncomeRatio: 0,
    foir: 0, // Fixed Obligation to Income Ratio

    // Bank Details (Internal)
    bankDetails: {
      accountNumber: "",
      ifscCode: "",
      accountHolderName: "",
      bankName: ""
    },

    // Conditions & Notes
    conditions: [],
    internalNotes: "",

    // Document Upload Status
    documentStatus: {
      aadharCardFront: { uploaded: false, verified: false, financeVerified: false, url: "" },
      aadharCardBack: { uploaded: false, verified: false, financeVerified: false, url: "" },
      borrowerPanCard: { uploaded: false, verified: false, financeVerified: false, url: "" },
      relationshipProof: { uploaded: false, verified: false, financeVerified: false, url: "" },
      healthcareLetter: { uploaded: false, verified: false, financeVerified: false, url: "" },
      cancelledCheque: { uploaded: false, verified: false, financeVerified: false, url: "" },
      incomeProof: { uploaded: false, verified: false, financeVerified: false, url: "" },
      insurancePolicy: { uploaded: false, verified: false, financeVerified: false, url: "" },
      hospitalBill: { uploaded: false, verified: false, financeVerified: false, url: "" },
      bankStatement: { uploaded: false, verified: false, financeVerified: false, url: "" },
      cibilReport: { uploaded: false, verified: false, financeVerified: false, url: "" },
      consentDocument: { uploaded: false, verified: false, financeVerified: false, url: "" },
      anyOtherDocument: { uploaded: false, verified: false, financeVerified: false, url: "" }
    },

    // Review Tracking
    reviewStartTime: null,
    reviewEndTime: null,
    reviewDuration: 0
  });

  // Credit Score & Verification State
  const [creditData, setCreditData] = useState({
    score: null,
    history: [],
    factors: [],
    recommendations: []
  });

  const [documentVerification, setDocumentVerification] = useState({
    aadhar: { verified: false, confidence: 0, issues: [] },
    pan: { verified: false, confidence: 0, issues: [] },
    income: { verified: false, confidence: 0, issues: [] },
    employment: { verified: false, confidence: 0, issues: [] },
    bank: { verified: false, confidence: 0, issues: [] }
  });

  const [financialHistory, setFinancialHistory] = useState({
    previousLoans: [],
    creditUtilization: 0,
    repaymentHistory: [],
    defaults: []
  });

  // Risk Assessment State
  const [riskAssessment, setRiskAssessment] = useState({
    overallRisk: "medium",
    riskScore: 0,
    factors: [],
    mitigationStrategies: []
  });

  // Query management state
  const [disputeStatus, setDisputeStatus] = useState({
    hasActiveDisputes: false,
    activeDisputes: [],
    canProceed: true
  });
  const [showDisputeManager, setShowDisputeManager] = useState(false);

  // Discussion state
  const [discussions, setDiscussions] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [timelineEvents, setTimelineEvents] = useState([]);

  // Internal comments state
  const [internalComments, setInternalComments] = useState("");
  const [savedInternalComments, setSavedInternalComments] = useState("");
  const [commentsSaving, setCommentsSaving] = useState(false);
  const [allInternalComments, setAllInternalComments] = useState([]);

  // UI State - matching PolicyApplicationDetails structure
  const [collapsedSections, setCollapsedSections] = useState({
    patient: true,
    medical: true,
    policy: false,
    hospital: true,
    financial: false,
    documents: false
  });

  // Document viewer state - matching PolicyApplicationDetails
  const [showDocumentPanel, setShowDocumentPanel] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showAllDocuments, setShowAllDocuments] = useState(false); // Toggle to show all documents vs latest only
  const scrollYBeforeDocRef = useRef(0);

  // Discussion panel state - matching PolicyApplicationDetails
  const [showDiscussionPanel, setShowDiscussionPanel] = useState(false);

  // Clarification tracking state - matching PolicyApplicationDetails
  const [hasPendingClarification, setHasPendingClarification] = useState(false);
  const [clarificationDetails, setClarificationDetails] = useState(null);

  // Document query state
  const [documentQueries, setDocumentQueries] = useState({});
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [queryDocumentKey, setQueryDocumentKey] = useState("");
  const [queryComment, setQueryComment] = useState("");

  // Credit Additional Info State
  const [creditAdditionalInfo, setCreditAdditionalInfo] = useState({
    customCreditScore: '',
    creditBureauName: 'CIBIL',
    creditReportDate: '',
    previousCreditHistory: '',
    defaultHistory: '',
    additionalNotes: [],
    customFields: []
  });

  // Auto-save status for credit info
  const [creditSaveStatus, setCreditSaveStatus] = useState({
    isSaving: false,
    lastSaved: null,
    error: null
  });

  // Sanctions and Letters state
  const [sanctions, setSanctions] = useState([]);
  const [showLetterModal, setShowLetterModal] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showFALPreview, setShowFALPreview] = useState(false);
  const [letterZoom, setLetterZoom] = useState(1);
  const letterScrollYRef = useRef(0);
  // Right panel scroll preservation
  const rightPanelRef = useRef(null);
  const rightPanelScrollRef = useRef(0);

  const tabs = [
    { id: "application", label: "Application Review", icon: FileText },
    { id: "financial", label: "Financial Assessment", icon: Calculator },
    { id: "documents", label: "Document Verification", icon: FileCheckIcon },
    { id: "credit", label: "Credit Analysis", icon: CreditCardIcon },
    {
      id: "disputes",
      label: "Query",
      icon: AlertTriangle,
      count: disputeStatus.activeDisputes.length,
      alert: disputeStatus.hasActiveDisputes
    },
    {
      id: "discussions",
      label: "Discussions",
      icon: MessageSquare,
      count: unreadCount,
    },
    { id: "sanctions", label: "Letters & Documents", icon: Award, count: null },
    { id: "status", label: "Status Timeline", icon: Activity },
  ];

  useEffect(() => {
    if (user?.uid && applicationId) {
      // Load and then evaluate using the freshly loaded application snapshot
      loadApplicationData().then((loadedApp) => {
        const appSnap = loadedApp || application; // prefer freshly loaded app to avoid stale state
        // After application loads, attempt starting review only if eligible
        const currentStatus = appSnap?.workflow?.financialVerification?.status;
        const topLevelStatus = appSnap?.status;
        const terminalStatuses = ["approved", "rejected", "pending_info"]; // workflow-financial level
        const topLevelTerminal = ["financial_approved", "financial_rejected", "pending_financial_clarification"];
        if (!terminalStatuses.includes(currentStatus) && !topLevelTerminal.includes(topLevelStatus) && !isPostFinanceLocked(appSnap)) {
          startFinancialReview(appSnap);
        } else {
          console.log('[FinancialReview] Not auto-starting review due to status:', currentStatus, topLevelStatus);
        }
      });
    }

    // Set up real-time listener
    const unsubscribe = subscribeToApplicationUpdates(
      applicationId,
      (updatedApp) => {
        setApplication(updatedApp);

        // Keep financial approval sticky once it's reached
        const remoteApproved =
          updatedApp?.workflow?.financialVerification?.status === "approved" ||
          updatedApp?.status === "financial_approved";
        setFinancialApprovedEffective((prev) => prev || Boolean(remoteApproved));

        // Also keep readOnly sticky unless reassignment challenge is active for finance
        const kypReassign = updatedApp?.workflow?.medicalVerification?.kypReassignment || null;
        const reassignedToFinance = (
          kypReassign && (
            kypReassign.target === 'finance' || (kypReassign.targets || [])?.includes?.('finance')
          )
        );
        const reassignStatus = kypReassign?.status || 'pending';
        const isReassignmentActiveForFinance = reassignedToFinance && !['finance_completed', 'completed'].includes(reassignStatus);
        const isChallengedForFinance = (
          updatedApp?.workflow?.medicalVerification?.kypHospitalAcknowledgement?.status === 'challenged' &&
          isReassignmentActiveForFinance
        );
        setIsReadOnly((prev) => (prev || remoteApproved) && !isChallengedForFinance);

        // Check if review is still active
        if (
          updatedApp.workflow?.financialVerification?.status === "in_progress" &&
          updatedApp.workflow?.financialVerification?.reviewerId === user.uid
        ) {
          if (!isTimerActive) {
            const startTime =
              updatedApp.workflow?.financialVerification?.reviewStartTime?.toDate();
            if (startTime) {
              setReviewStartTime(startTime);
              startTimer(startTime);
            }
          }
        } else {
          stopTimer();
        }
      }
    );

    return () => {
      unsubscribe();
      stopTimer();
      if (activityRef.current) {
        clearInterval(activityRef.current);
      }
    };
  }, [applicationId, user?.uid]);

  useEffect(() => {
    if (activeTab === "discussions") {
      loadDiscussions();
      markAllMessagesAsRead();
    }
  }, [activeTab]);

  // Load document queries
  useEffect(() => {
    if (applicationId) {
      loadDocumentQueries();
    }
  }, [applicationId]);

  // Sync creditAdditionalInfo with financialAssessment
  useEffect(() => {
    if (financialAssessment.creditAdditionalInfo) {
      setCreditAdditionalInfo(financialAssessment.creditAdditionalInfo);
    }
  }, [financialAssessment.creditAdditionalInfo]);

  useEffect(() => {
    if (isTimerActive) {
      activityRef.current = setInterval(() => {
        updateReviewActivity(applicationId, user.uid);
      }, 2 * 60 * 1000); // 2 minutes

      return () => {
        if (activityRef.current) {
          clearInterval(activityRef.current);
        }
      };
    }
  }, [isTimerActive, applicationId, user.uid]);

  // Confirm before leaving or navigating away (browser back/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ""; // triggers native confirm
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Push a dummy state on mount to detect back button
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      const confirmed = window.confirm('Are you sure you want to leave this page? Unsaved changes may be lost.');
      if (!confirmed) {
        // Push the state again to prevent navigation
        window.history.pushState(null, '', window.location.href);
      } else {
        // Allow navigation by not preventing default
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        // Navigate explicitly to All Applications within dashboard (with header/menu)
        navigate('/finance/dashboard?section=all-applications');
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const loadApplicationData = async () => {
    setLoading(true);
    try {
      const result = await getPatientApplication(applicationId);
      if (result.success) {
        const app = result.application;
        setApplication(app);

        try { await loadUnifiedTimeline(app); } catch (_) {}

        // Determine readOnly based on approval status
        const finApprovedBase = app?.workflow?.financialVerification?.status === 'approved' || app?.status === 'financial_approved';
        setFinancialApprovedEffective((prev) => prev || Boolean(finApprovedBase));
        // Override: enable editing if KYP challenged and reassigned to Finance
        // Only while reassignment is actively pending for Finance; lock again when completed
        const kypReassign = app?.workflow?.medicalVerification?.kypReassignment || null;
        const reassignedToFinance = (
          kypReassign && (
            kypReassign.target === 'finance' || (kypReassign.targets || [])?.includes?.('finance')
          )
        );
        const reassignStatus = kypReassign?.status || 'pending';
        const isReassignmentActiveForFinance = reassignedToFinance && !['finance_completed', 'completed'].includes(reassignStatus);
        const isChallengedForFinance = (
          app?.workflow?.medicalVerification?.kypHospitalAcknowledgement?.status === 'challenged' &&
          isReassignmentActiveForFinance
        );
        setIsReadOnly((prev) => (prev || finApprovedBase) && !isChallengedForFinance);

        // Check dispute status
        await checkDisputeStatus();

        // Load existing financial assessment if available
        const existingAssessment = result.application.workflow?.financialVerification?.assessment;
        if (existingAssessment) {
          setFinancialAssessment(prev => ({
            ...prev,
            ...existingAssessment,
            // Handle backward compatibility
            riskCategory: existingAssessment.riskCategory || existingAssessment.riskAssessment,
          }));

          // Load credit additional info from existing assessment
          if (existingAssessment.creditAdditionalInfo) {
            setCreditAdditionalInfo(existingAssessment.creditAdditionalInfo);
          }
        } else {
          // Initialize with application data for first-time review - comprehensive population
          const app = result.application;
          const estimatedCost = app.medical?.estimatedCost || app.requestedLoanAmount || 0;

          setFinancialAssessment(prev => ({
            ...prev,
            // Personal Details from application
            isPatientBorrowerSame: app.isPatientBorrowerSame || false,
            patientName: app.patient?.name || app.patientName || "",
            borrowerName: app.borrowerName || app.patient?.name || "",
            borrowerDateOfBirth: app.borrowerDateOfBirth || app.patient?.dateOfBirth || "",
            emailId: app.emailId || app.patient?.email || "",
            contactNumber: app.contactNumber || app.patient?.phone || "",
            alternateContactNumber: app.alternateContactNumber || "",
            relationToBorrower: app.relationToBorrower || "Self",
            occupation: app.occupation || app.patient?.occupation || "",
            companyName: app.companyName || app.patient?.companyName || "",
            aadharNumber: app.aadharNumber || app.patient?.aadharNumber || "",
            panNumber: app.panNumber || app.patient?.panNumber || "",
            patientGender: app.patientGender || app.patient?.gender || "",
            borrowerGender: app.borrowerGender || app.patient?.gender || "",
            borrowerMaritalStatus: app.borrowerMaritalStatus || "",

            // Address Details
            isCurrentPermanentSame: app.isCurrentPermanentSame || false,
            permanentAddress: {
              pincode: app.permanentPincode || app.patient?.address?.pincode || "",
              state: app.permanentState || app.patient?.address?.state || "",
              district: app.permanentDistrict || app.patient?.address?.district || "",
              city: app.permanentCity || app.patient?.address?.city || "",
              address: app.permanentAddress || app.patient?.address?.street || ""
            },
            currentAddress: {
              pincode: app.currentPincode || app.patient?.address?.pincode || "",
              state: app.currentState || app.patient?.address?.state || "",
              district: app.currentDistrict || app.patient?.address?.district || "",
              city: app.currentCity || app.patient?.address?.city || "",
              address: app.currentAddress || app.patient?.address?.street || ""
            },

            // Reference Details
            reference: {
              name: app.referenceName || "",
              contactNumber: app.referenceContactNumber || "",
              relationship: app.referenceRelationship || "",
              emailId: app.referenceEmailId || ""
            },

            // Hospital Information
            hospital: {
              name: app.hospitalName || "",
              bankAccountNumber: app.hospitalBankAccountNumber || "",
              bankName: app.hospitalBankName || "",
              beneficiaryName: app.beneficiaryHospitalName || "",
              branchName: app.hospitalBankBranchName || "",
              ifscCode: app.hospitalBankIFSC || "",
              aggregatorName: app.hospitalAggregatorName || "Abhiraksha"
            },

            // Treatment Details
            treatmentDetails: app.treatmentDetails || app.medical?.diagnosis || "",

            // Claim Processing Form
            claimProcessing: {
              insuranceCompany: app.insuranceCompany || app.policy?.insuranceCompany || "",
              isInHouseTPA: app.isInHouseTPA || false,
              tpa: app.tpa || "",
              uhidNumber: app.uhidNumber || "",
              policyNumber: app.policyNumber || app.policy?.number || "",
              sumInsuredAmount: app.sumInsuredAmount || app.policy?.coverageAmount || 0,
              isCorporatePolicy: app.isCorporatePolicy || false,
              policyStartDate: app.policyStartDate || app.policy?.startDate || "",
              policyEndDate: app.policyEndDate || app.policy?.endDate || ""
            },

            // Bank Details (Cancel Cheque Details)
            borrowerBank: {
              bankName: app.borrowerBankName || "",
              branchName: app.borrowerBankBranch || "",
              accountNumber: app.borrowerAccountNumber || "",
              ifscCode: app.borrowerIFSCCode || ""
            },

            // Loan Details
            loanDetails: {
              totalMonthlyIncome: app.totalIncome || app.patient?.financialInfo?.monthlyIncome || 0,
              schemeMonths: app.schemeMonths || 12,
              interestSubventionPercent: app.interestSubventionPercent || 0,
              processingFeePercent: app.processingFeePercent || 0
            },

            // Financial Assessment
            eligibleAmount: estimatedCost,
            recommendedAmount: estimatedCost * 0.9, // Default to 90% of requested
            monthlyIncome: app.totalIncome || app.patient?.financialInfo?.monthlyIncome || 0,
            existingEMI: app.patient?.financialInfo?.existingEMI || 0,

            // Document Status from uploaded documents
            documentStatus: {
              aadharCardFront: {
                uploaded: !!app.documents?.aadharCardFront || !!app.aadharCardFront,
                verified: false,
                financeVerified: false,
                url: app.documents?.aadharCardFront?.url || app.aadharCardFront || ""
              },
              aadharCardBack: {
                uploaded: !!app.documents?.aadharCardBack || !!app.aadharCardBack,
                verified: false,
                financeVerified: false,
                url: app.documents?.aadharCardBack?.url || app.aadharCardBack || ""
              },
              borrowerPanCard: {
                uploaded: !!app.documents?.borrowerPanCard || !!app.borrowerPanCard,
                verified: false,
                financeVerified: false,
                url: app.documents?.borrowerPanCard?.url || app.borrowerPanCard || ""
              },
              relationshipProof: {
                uploaded: !!app.documents?.relationshipProof || !!app.relationshipProof,
                verified: false,
                financeVerified: false,
                url: app.documents?.relationshipProof?.url || app.relationshipProof || ""
              },
              healthcareLetter: {
                uploaded: !!app.documents?.healthcareLetter || !!app.healthcareLetter,
                verified: false,
                financeVerified: false,
                url: app.documents?.healthcareLetter?.url || app.healthcareLetter || ""
              },
              cancelledCheque: {
                uploaded: !!app.documents?.cancelledCheque || !!app.cancelledCheque,
                verified: false,
                financeVerified: false,
                url: app.documents?.cancelledCheque?.url || app.cancelledCheque || ""
              },
              incomeProof: {
                uploaded: !!app.documents?.incomeProof || !!app.incomeProof,
                verified: false,
                financeVerified: false,
                url: app.documents?.incomeProof?.url || app.incomeProof || ""
              },
              insurancePolicy: {
                uploaded: !!app.documents?.insurancePolicy || !!app.insurancePolicy,
                verified: false,
                financeVerified: false,
                url: app.documents?.insurancePolicy?.url || app.insurancePolicy || ""
              },
              hospitalBill: {
                uploaded: !!app.documents?.hospitalBill || !!app.hospitalBill,
                verified: false,
                financeVerified: false,
                url: app.documents?.hospitalBill?.url || app.hospitalBill || ""
              },
              bankStatement: {
                uploaded: !!app.documents?.bankStatement || !!app.bankStatement,
                verified: false,
                financeVerified: false,
                url: app.documents?.bankStatement?.url || app.bankStatement || ""
              },
              cibilReport: {
                uploaded: !!app.documents?.cibilReport || !!app.cibil,
                verified: false,
                financeVerified: false,
                url: app.documents?.cibilReport?.url || app.cibil || ""
              },
              consentDocument: {
                uploaded: !!app.documents?.consentDocument || !!app.consentDocument,
                verified: false,
                financeVerified: false,
                url: app.documents?.consentDocument?.url || app.consentDocument || ""
              },
              anyOtherDocument: {
                uploaded: !!app.documents?.anyOtherDocument || !!app.anyOtherDocument,
                verified: false,
                financeVerified: false,
                url: app.documents?.anyOtherDocument?.url || app.anyOtherDocument || ""
              }
            }
          }));
        }

        // Load existing internal comments
        await loadFinancialComments();

        // Calculate unread messages
        calculateUnreadMessages(result.application);

        // Auto-fetch credit score if not already done
        if (!existingAssessment?.creditScore) {
          await handleFetchCreditScore(result.application);
        }

        // Auto-verify documents if not already done
        if (!existingAssessment?.documentsAuthentic) {
          await handleVerifyDocuments();
        }
      }
      // Return the loaded application object for callers that need a fresh snapshot
      return result.success ? result.application : null;
    } catch (error) {
      console.error("Error loading application:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnifiedTimeline = async (app) => {
    const safeToDate = (ts) => {
      if (!ts) return null; try { return ts?.toDate ? ts.toDate() : new Date(ts); } catch (_) { return null; }
    };
    const events = [];
    if (app?.submittedAt) events.push({ id:'submission', type:'submission', title:'Application Submitted', description:'Hospital submitted the patient application', timestamp: safeToDate(app.submittedAt), completed:true });
    const wf = app?.workflow || {};
    const map = [
      { key: 'medicalVerification', type: 'medical_review', title: 'Medical Review' },
      { key: 'policyVerification', type: 'policy_review', title: 'Policy Verification' },
      { key: 'financialVerification', type: 'financial_review', title: 'Financial Verification' },
      { key: 'kycVerification', type: 'kyc_verification', title: 'KYC Verification' },
    ];
    map.forEach(({ key, type, title }) => {
      const node = wf[key]; if (!node) return; const ts = node.verifiedAt || node.updatedAt || node.approvedAt; if (!ts) return;
      events.push({ id:type, type, title, description: String(node.status||'').replace('_',' '), timestamp: safeToDate(ts), completed: node.status==='approved', actor: node.verifiedBy ? { name: node.verifiedBy, team: type.includes('financial')?'financial': type.includes('policy')?'policy': type.includes('medical')?'medical': undefined } : undefined });
    });
    if (app?.statusHistory && typeof app.statusHistory==='object') {
      Object.entries(app.statusHistory).forEach(([status,data])=>{
        events.push({ id:`status-${status}`, type:'status_change', title:`Status changed to ${status.replace('_',' ').toUpperCase()}`, description: data?.comments || (data?.updatedBy?`Updated by ${data.updatedBy}`:''), timestamp: safeToDate(data?.updatedAt), actor: data?.updatedBy?{ name: data.updatedBy }:undefined, completed:true });
      });
    }
    const [disputesRes, docQueriesRes] = await Promise.all([
      getApplicationDisputes(app.applicationId),
      getDocumentQueries(app.applicationId),
    ]);
    if (disputesRes?.success) {
      disputesRes.disputes.forEach((d)=>{
        if (d.raisedAt) events.push({ id:`dispute-${d.id}-raised`, type:'dispute_raised', title:d.title||'Query Raised', description:d.description, timestamp:safeToDate(d.raisedAt), actor:d.raisedBy?{ name:d.raisedBy.userName, team:d.raisedBy.department }:undefined, completed:false });
        if (d.acknowledgedAt) events.push({ id:`dispute-${d.id}-ack`, type:'acknowledged', title:'Query Acknowledged', description:'', timestamp:safeToDate(d.acknowledgedAt), completed:false });
        if (d.resolvedAt) events.push({ id:`dispute-${d.id}-resolved`, type:'dispute_resolved', title:'Query Resolved', description:d.resolution||'Resolution recorded', timestamp:safeToDate(d.resolvedAt), actor:d.resolvedBy?{ name:d.resolvedBy.userName, team:d.resolvedBy.department }:undefined, attachments:Array.isArray(d.resolverDocuments)?d.resolverDocuments.map(f=>({name:f?.name||'Document', url:f?.url})).filter(a=>a.url):[], completed:true });
      });
    }
    if (docQueriesRes?.success) {
      docQueriesRes.queries.forEach((q)=>{
        if (q.raisedAt) events.push({ id:`docq-${q.id}-open`, type:'doc_query_open', title:`Document Query: ${q.documentTitle||q.documentKey}`, description:q.comment, timestamp:safeToDate(q.raisedAt), actor:q.raisedBy?{ name:q.raisedBy.name, team:q.raisedBy.team }:undefined, completed:q.status==='resolved' });
        if (q.status==='resolved' && q.resolvedAt) events.push({ id:`docq-${q.id}-resolved`, type:'doc_query_resolved', title:`Document Updated: ${q.documentTitle||q.documentKey}`, description:'Query resolved with updated document', timestamp:safeToDate(q.resolvedAt), actor:q.resolvedBy?{ name:q.resolvedBy.name, team:'hospital' }:undefined, attachments:q.updatedDocumentUrl?[{ name:q.updatedDocumentFileName||'Updated Document', url:q.updatedDocumentUrl }]:[], completed:true });
      });
    }
    events.sort((a,b)=> (b.timestamp?new Date(b.timestamp).getTime():0) - (a.timestamp?new Date(a.timestamp).getTime():0));
    setTimelineEvents(events);
  };

  // Reset sticky state when moving to a different application
  useEffect(() => {
    setFinancialApprovedEffective(false);
  }, [applicationId]);

  const startFinancialReview = async (appArg = null) => {
    try {
      const currentApp = appArg || application;
      // Guard: do not downgrade if already terminal or in another review
      const currentStatus = currentApp?.workflow?.financialVerification?.status;
      const topLevelStatus = currentApp?.status;
      const terminalStatuses = ["approved", "rejected", "pending_info"]; // workflow-financial level
      const topLevelTerminal = ["financial_approved", "financial_rejected", "pending_financial_clarification"];

      if (terminalStatuses.includes(currentStatus) || topLevelTerminal.includes(topLevelStatus) || isPostFinanceLocked(currentApp)) {
        console.log('[FinancialReview] Skipping startFinancialReview: terminal status present', currentStatus, topLevelStatus);
        return;
      }

      // If already in progress by another reviewer, do not override
      const existingReviewer = currentApp?.workflow?.financialVerification?.reviewerId;
      if (currentStatus === 'in_progress' && existingReviewer && existingReviewer !== user.uid) {
        console.warn('[FinancialReview] Another reviewer is already in progress, not taking over. Reviewer:', existingReviewer);
        return;
      }

      // If already in_progress by same reviewer and timer running, skip
      if (currentStatus === 'in_progress' && existingReviewer === user.uid) {
        console.log('[FinancialReview] Review already in progress for this user.');
        return;
      }

      const startTime = new Date();
      await updateFinancialStatus(applicationId, {
        status: "in_progress",
        reviewerId: user.uid,
        reviewerName: user.profile?.name || user.email,
        reviewStartTime: startTime
      });

      setReviewStartTime(startTime);
      startTimer(startTime);
    } catch (error) {
      console.error("Error starting financial review:", error);
    }
  };

  const checkDisputeStatus = async () => {
    try {
      const result = await checkApplicationDisputeStatus(applicationId, { department: 'financial' });
      if (result.success) {
        setDisputeStatus({
          hasActiveDisputes: result.hasActiveDisputes,
          activeDisputes: result.activeDisputes,
          canProceed: result.canProceed
        });
      }
    } catch (error) {
      console.error("Error checking dispute status:", error);
    }
  };

  const loadDiscussions = async () => {
    try {
      const result = await getApplicationDiscussions(applicationId);
      if (result.success) {
        setDiscussions(result.discussions);
        calculateUnreadMessages({ discussions: result.discussions });
      }
    } catch (error) {
      console.error("Error loading discussions:", error);
    }
  };

  const loadSanctions = useCallback(async () => {
    try {
      const result = await getApplicationSanctions(applicationId);
      if (result.success) {
        setSanctions(result.sanctions);
      }
    } catch (error) {
      console.error('Error loading sanctions:', error);
    }
  }, [applicationId]);

  const loadDocumentQueries = async () => {
    try {
      const queries = await getDocumentQueries(applicationId);
      const queriesByDoc = {};
      queries.forEach(query => {
        if (!queriesByDoc[query.documentKey]) {
          queriesByDoc[query.documentKey] = [];
        }
        queriesByDoc[query.documentKey].push(query);
      });
      setDocumentQueries(queriesByDoc);
    } catch (error) {
      console.error("Error loading document queries:", error);
    }
  };

  const openQueryModal = (documentKey) => {
    setQueryDocumentKey(documentKey);
    setShowQueryModal(true);
  };

  const handleRaiseQuery = async () => {
    if (!queryComment.trim()) {
      alert("Please enter a comment");
      return;
    }

    try {
      await raiseDocumentQuery(
        applicationId,
        queryDocumentKey,
        queryComment,
        {
          uid: user.uid,
          name: user.displayName || user.email,
          email: user.email,
          team: 'finance'
        }
      );

      await loadDocumentQueries();
      setShowQueryModal(false);
      setQueryComment("");
      setQueryDocumentKey("");
      alert("Query raised successfully");
    } catch (error) {
      console.error("Error raising query:", error);
      alert("Failed to raise query");
    }
  };

  useEffect(() => {
    if (activeTab === 'sanctions') {
      loadSanctions();
    }
  }, [activeTab, loadSanctions]);

  // Robust download helper (forces actual download via Blob)
  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to direct window.open
      window.open(url, '_blank');
    }
  };

  const openLetter = async (letter) => {
    try {
      // Handle KYP letters by generating a preview blob URL
      if (letter?.isKYPLetter || letter?.type === 'kyp') {
        // Generate KYP PDF for preview
        const { generateKYPPDF } = await import('../../utils/pdfUtils');
        const result = await generateKYPPDF(application, letter.kypData, { template: 'comprehensive' });
        
        if (result?.success && result?.blob) {
          const previewUrl = URL.createObjectURL(result.blob);
          const letterWithPreview = {
            ...letter,
            url: previewUrl,
            fileName: result.fileName || 'KYP-Letter.pdf',
            _isPreviewUrl: true // Flag to clean up later
          };
          setSelectedLetter(letterWithPreview);
          setLetterZoom(1);
          setShowLetterModal(true);
          // Preserve current scroll on open
          letterScrollYRef.current = window.scrollY;
          setTimeout(() => window.scrollTo(0, letterScrollYRef.current || 0), 0);
        } else {
          // Fallback if PDF generation fails
          setSelectedLetter(letter);
          setLetterZoom(1);
          setShowLetterModal(true);
          letterScrollYRef.current = window.scrollY;
          setTimeout(() => window.scrollTo(0, letterScrollYRef.current || 0), 0);
        }
      } else if (letter?.type === 'approval' || letter?.id === 'approval' || (letter?.title || '').toLowerCase().includes('final approval')) {
        // Use React-PDF FALPreviewModal for modern preview format
        setShowFALPreview(true);
      } else {
        setSelectedLetter(letter);
        setLetterZoom(1);
        setShowLetterModal(true);
        letterScrollYRef.current = window.scrollY;
        setTimeout(() => window.scrollTo(0, letterScrollYRef.current || 0), 0);
      }
    } catch (error) {
      console.error('Error opening letter:', error);
      // Fallback to original behavior
      if (letter?.type === 'approval' || letter?.id === 'approval') {
        setShowFALPreview(true);
      } else {
        setSelectedLetter(letter);
        setShowLetterModal(true);
      }
    }
  };

  const handleLetterDownload = async (letter) => {
    try {
      if (letter?.isKYPLetter || letter?.type === 'kyp') {
        // Handle KYP letter download (if supported)
        const { downloadKYPPDFFromFirestore, downloadKYPPDF } = await import('../../utils/pdfUtils');
        const byId = await downloadKYPPDFFromFirestore(application?.applicationId, { template: 'comprehensive' });
        if (!byId?.success) {
          const fileName = `KYP_Comprehensive_${application?.applicationId || Date.now()}_${Date.now()}.pdf`;
          const res = await downloadKYPPDF(application, letter.kypData, fileName, { template: 'comprehensive' });
          if (!res?.success) throw new Error(res?.error || 'Failed to generate KYP letter');
        }
      } else if (letter?.type === 'approval' || letter?.id === 'approval' || (letter?.title || '').toLowerCase().includes('final approval')) {
        // Handle FAL download (if supported)
        const remarks = application?.workflow?.dischargeSettlement?.comments || '';
        const { downloadFALPDF } = await import('../../utils/pdfUtils');
        const res = await downloadFALPDF(application, { remarks, template: 'comprehensive' });
        if (!res?.success) throw new Error(res?.error || 'Failed to generate FAL');
      } else {
        // Regular letter download
        await downloadFile(letter.url, letter.fileName || `${letter.title || 'letter'}`);
      }
    } catch (error) {
      console.error('Error downloading letter:', error);
      alert('Failed to download letter. Please try again.');
    }
  };

  // Generate a letter on-demand when it's available by status but not yet in history
  const generateAndDownloadLetter = async (letterId) => {
    setActionLoading((prev) => ({ ...prev, [letterId]: true }));
    try {
      if (letterId === 'kyp') {
        const { downloadKYPPDFFromFirestore, downloadKYPPDF } = await import('../../utils/pdfUtils');
        const byId = await downloadKYPPDFFromFirestore(application?.applicationId, { template: 'comprehensive' });
        if (!byId?.success) {
          const kypData = application?.workflow?.medicalVerification?.kypData || {};
          const fileName = `KYP_Comprehensive_${application?.applicationId || Date.now()}_${Date.now()}.pdf`;
          const res = await downloadKYPPDF(application, kypData, fileName, { template: 'comprehensive' });
          if (!res?.success) throw new Error(res?.error || 'Failed to generate KYP letter');
        }
      } else if (letterId === 'approval') {
        const remarks = application?.workflow?.dischargeSettlement?.comments || '';
        const { downloadFALPDF } = await import('../../utils/pdfUtils');
        const res = await downloadFALPDF(application, { remarks, template: 'comprehensive' });
        if (!res?.success) throw new Error(res?.error || 'Failed to generate FAL');
      } else if (letterId === 'disbursal' || letterId === 'closure') {
        alert('Generation not supported from Financial Review for this document.');
      }

      // Refresh sanctions history after generation attempt
      await loadSanctions();
    } catch (error) {
      console.error('Error generating letter:', error);
      alert(error?.message || 'Failed to generate letter');
    } finally {
      setActionLoading((prev) => ({ ...prev, [letterId]: false }));
    }
  };

  const loadFinancialComments = async () => {
    try {
      const result = await getFinancialComments(applicationId);
      if (result.success) {
        // Load internal comments
        setAllInternalComments(result.comments || []);
        
        // Load current user's comments
        const currentUserComment = result.comments?.find(comment => comment.userId === user.uid);
        if (currentUserComment) {
          setInternalComments(currentUserComment.comments);
          setSavedInternalComments(currentUserComment.comments);
        }
        
        // Load credit additional info
        if (result.creditAdditionalInfo) {
          setCreditAdditionalInfo(result.creditAdditionalInfo);
          
          // Update save status if we have saved data
          if (result.lastUpdatedAt) {
            setCreditSaveStatus({
              isSaving: false,
              lastSaved: new Date(result.lastUpdatedAt),
              error: null
            });
          }
        }
        
        // Load document verification status
        if (result.documentStatus && Object.keys(result.documentStatus).length > 0) {
          setFinancialAssessment(prev => ({
            ...prev,
            documentStatus: {
              ...prev.documentStatus,
              ...result.documentStatus
            }
          }));
        }
        
        // Load complete assessment data if available
        if (result.assessment && Object.keys(result.assessment).length > 0) {
          setFinancialAssessment(prev => ({
            ...prev,
            ...result.assessment
          }));
          
          // Update credit additional info from assessment if it exists there
          if (result.assessment.creditAdditionalInfo) {
            setCreditAdditionalInfo(result.assessment.creditAdditionalInfo);
          }
        }

        // Load auto-saved data from localStorage if available
        const savedDataKey = `financial_review_${applicationId}`;
        const savedData = localStorage.getItem(savedDataKey);
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            if (parsedData && Object.keys(parsedData).length > 0) {
              setFinancialAssessment(prev => ({
                ...prev,
                ...parsedData,
                lastSavedAt: parsedData.savedAt || prev.lastSavedAt
              }));
              console.log('Loaded auto-saved financial assessment data from localStorage');
            }
          } catch (error) {
            console.error('Error parsing saved financial assessment data:', error);
          }
        }
        
        console.log('Loaded financial data from Firestore:', {
          commentsCount: result.comments?.length || 0,
          hasCreditInfo: !!result.creditAdditionalInfo,
          hasDocumentStatus: !!result.documentStatus,
          hasAssessment: !!result.assessment
        });
      }
    } catch (error) {
      console.error("Error loading financial data:", error);
    }
  };  const calculateUnreadMessages = (appData) => {
    if (!appData || !appData.discussions) return;

    let unread = 0;
    appData.discussions.forEach((discussion) => {
      discussion.messages?.forEach((message) => {
        if (!message.readBy?.includes(user.uid)) {
          unread++;
        }
      });
    });
    setUnreadCount(unread);
  };

  const markAllMessagesAsRead = async () => {
    try {
      await markMessagesAsRead(applicationId, user.uid);
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const startTimer = (startTime = null) => {
    const start = startTime || reviewStartTime || new Date();
    if (!reviewStartTime && !startTime) {
      setReviewStartTime(start);
    }
    setIsTimerActive(true);

    timerRef.current = setInterval(() => {
      const now = new Date();
      const elapsed = Math.floor((now - start) / 1000);
      setElapsedTime(elapsed);
    }, 1000);
  };

  const stopTimer = () => {
    setIsTimerActive(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (activityRef.current) {
      clearInterval(activityRef.current);
      activityRef.current = null;
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "submitted":
      case "medical_approved":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "financial_review_pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "financial_approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "financial_rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "pending_financial_clarification":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Handler functions for PolicyApplicationDetails-style layout
  const handleUnreadCountChange = (count) => {
    setUnreadCount(count);
  };

  // Credit Score Fetching
  const handleFetchCreditScore = async (application) => {
    setActionLoading(prev => ({ ...prev, creditScore: true }));
    try {
      // Handle different possible data structures for PAN/Aadhar
      console.log("Fetching credit score for application:", application);
      const panNumber = application?.patient?.kyc?.panNumber;

      const aadharNumber = application?.patient?.kyc?.aadharNumber;

      if (!panNumber && !aadharNumber) {
        alert("PAN or Aadhar number not found in application data");
        return;
      }

      const result = await fetchCreditScore(panNumber, aadharNumber);

      if (result.success) {
        setCreditData(result.creditData);
        setFinancialAssessment(prev => ({
          ...prev,
          creditScore: result.creditData.score,
          creditRating: result.creditData.rating
        }));

        // Auto-calculate risk based on credit score
        await calculateRiskAssessment(result.creditData.score);
      }
    } catch (error) {
      console.error("Error fetching credit score:", error);
      alert("Failed to fetch credit score");
    } finally {
      setActionLoading(prev => ({ ...prev, creditScore: false }));
    }
  };

  // Document Verification
  const handleVerifyDocuments = async () => {
    setActionLoading(prev => ({ ...prev, documents: true }));
    try {
      // Handle different possible data structures for documents
      const documents = application?.documents ||
        application?.documentsUploaded ||
        application?.uploadedDocuments || {};

      if (!documents || Object.keys(documents).length === 0) {
        return;
      }

      const verificationResults = {};

      // Verify each document type
      for (const [docType, docData] of Object.entries(documents)) {
        if (docData && (docData.url || docData.downloadURL)) {
          const docUrl = docData.url || docData.downloadURL;
          const result = await verifyDocumentAuthenticity(docUrl, docType);
          verificationResults[docType] = result;
        }
      }

      if (Object.keys(verificationResults).length === 0) {
        alert("No valid document URLs found for verification");
        return;
      }

      setDocumentVerification(verificationResults);

      // Update overall document verification status
      const overallAuthenticity = Object.values(verificationResults)
        .every(result => result.verified && result.confidence > 0.8);

      setFinancialAssessment(prev => ({
        ...prev,
        documentsAuthentic: overallAuthenticity
      }));

    } catch (error) {
      console.error("Error verifying documents:", error);
      alert("Failed to verify documents");
    } finally {
      setActionLoading(prev => ({ ...prev, documents: false }));
    }
  };

  // Financial Risk Assessment
  const calculateRiskAssessment = async (creditScore) => {
    setActionLoading(prev => ({ ...prev, riskAssessment: true }));
    try {
      // Check if application data is available
      if (!application) {
        console.warn("Application data not available for risk assessment");
        return;
      }

      const riskData = await calculateFinancialRisk({
        creditScore,
        income: application.patient?.financialInfo?.monthlyIncome || 0,
        employment: application.patient?.financialInfo?.employmentType || "",
        loanAmount: application.medical?.estimatedCost || 0,
        existingDebts: application.patient?.financialInfo?.existingLoans || 0
      });

      setRiskAssessment(riskData);
      setFinancialAssessment(prev => ({
        ...prev,
        riskAssessment: riskData.overallRisk,
        eligibleAmount: riskData.recommendedAmount,
        interestRate: riskData.suggestedInterestRate
      }));

    } catch (error) {
      console.error("Error calculating risk assessment:", error);
    } finally {
      setActionLoading(prev => ({ ...prev, riskAssessment: false }));
    }
  };

  // Save Internal Comments
  const handleSaveInternalComments = async () => {
    if (!internalComments.trim()) {
      alert("Please enter some comments before saving");
      return;
    }

    setCommentsSaving(true);
    try {
      await saveFinancialComments(
        applicationId,
        user.uid,
        internalComments,
        user.profile?.name || user.email || "Financial Team"
      );

      setSavedInternalComments(internalComments);
      await loadFinancialComments();
      alert("Internal comments saved successfully");
    } catch (error) {
      console.error("Error saving internal comments:", error);
      alert("Failed to save internal comments");
    } finally {
      setCommentsSaving(false);
    }
  };

  // Complete Financial Review
  const handleCompleteReview = async () => {
    if (!reviewDecision || !reviewComments.trim()) {
      alert("Please provide review decision and comments");
      return;
    }

    if (disputeStatus.hasActiveDisputes) {
      alert('Cannot complete review while there are active disputes. Please resolve all disputes first.');
      return;
    }

    setActionLoading(prev => ({ ...prev, complete: true }));
    try {
      // Create clean data for saving (exclude File objects)
      const cleanAssessment = {
        ...financialAssessment,
        // Remove any potential File objects from assessment
        documents: undefined // Don't include document objects
      };

      const cleanCreditData = {
        ...creditData,
        // Ensure no File objects in credit data
        documents: undefined
      };

      const cleanDocumentVerification = {
        ...documentVerification,
        // Only include verification results, not actual files
      };

      const result = await completeFinancialVerification(applicationId, user.uid, {
        status: reviewDecision, // approved, rejected, pending_info
        decision: reviewDecision,
        comments: reviewComments,
        assessment: cleanAssessment,
        creditData: cleanCreditData,
        documentVerification: cleanDocumentVerification,
        riskAssessment,
        reviewerId: user.uid,
        reviewerName: user.profile?.name || user.email,
        completedAt: new Date(),
        processingTime: Math.floor(elapsedTime / 60) // in minutes
      });

      if (result.success) {
        alert("Financial review completed successfully!");
        navigate("/finance/dashboard?section=all-applications");
      }
    } catch (error) {
      console.error("Error completing review:", error);
      alert("Failed to complete review");
    } finally {
      setActionLoading(prev => ({ ...prev, complete: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Application not found</p>
          <button
            onClick={() => navigate("/finance/dashboard?section=all-applications")}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Applications
          </button>
        </div>
      </div>
    );
  }

  // Enhanced Financial Review - matching PolicyApplicationDetails structure
  const renderEnhancedFinancialReview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" style={{ height: 'calc(100vh - 200px)' }}>
      {/* Left Side - Financial Review Form (60%) */}
      <div className="lg:col-span-3 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 pr-2" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        <div className="space-y-6">
          {/* Medical Information Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 p-2 rounded-lg">
                  <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Medical Information</h3>
              </div>
            </div>

            <div className="space-y-6">
              {/* Diagnosis Update */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div className="bg-red-100 p-1 rounded">
                      <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-red-800">Diagnosis Update</h4>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                    {(() => {
                      const medicalStatus = application?.workflow?.medicalVerification?.status;
                      const approvedForProcess = application?.workflow?.medicalVerification?.approvedForProcess;
                      const applicationStatus = application?.status;
                      const medicalApproval = application?.medical?.approval?.status;
                      const doctorApproval = application?.medical?.doctorApproval;
                      const doctorReviewStatus = application?.workflow?.doctorReview?.status;
                      const verifiedAt = application?.workflow?.medicalVerification?.verifiedAt;
                      
                      // Check multiple conditions for approval - comprehensive list
                      const isApproved = 
                        medicalStatus === 'approved' || 
                        medicalStatus === 'medical_approved' ||
                        approvedForProcess === true ||
                        applicationStatus === 'approved_for_process' ||
                        applicationStatus === 'medical_approved' ||
                        applicationStatus === 'approved' ||
                        medicalApproval === 'approved' ||
                        medicalApproval === 'medical_approved' ||
                        doctorApproval === true ||
                        doctorReviewStatus === 'approved' ||
                        doctorReviewStatus === 'medical_approved' ||
                        Boolean(verifiedAt);
                      
                      if (isApproved) {
                        return (
                          <span className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">
                            APPROVED
                          </span>
                        );
                      } else {
                        return (
                          <span className="text-sm bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-semibold">
                            PENDING
                          </span>
                        );
                      }
                    })()}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Condition:</span>
                    <span className="text-sm font-semibold text-red-700">ACUTE GASTRITIS WITH VERTIGO-DEHYDRATION</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">ICD Code:</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">K29.00</span>
                    <span className="text-sm text-gray-600">- Acute gastritis without bleeding</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Type:</span>
                    <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">medical</span>
                  </div>
                </div>
              </div>

              {/* Treatment Details */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="bg-green-100 p-1 rounded">
                    <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-green-800">Treatment Details</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Procedure:</span>
                    <span className="text-sm font-semibold text-green-700">MEDICAL MANAGEMENT</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Last reviewed by:</span>
                    <span className="text-sm font-semibold text-gray-800">Muni Bhargavi</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Review Date:</span>
                    <span className="text-sm text-gray-600">10/16/2025</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 p-2 rounded-lg">
                  <Calculator className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Financial Assessment
                </h2>
              </div>
              {financialAssessment.lastSavedAt && (
                <div className="text-sm text-gray-500 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
                  Last saved {new Date(financialAssessment.lastSavedAt).toLocaleTimeString()}
                </div>
              )}
            </div>

              <FinancialReviewForm
              application={application}
              onSubmit={async (reviewData) => {
                try {
                  console.log('Received review data:', reviewData);
                  
                  // Update financial assessment state
                  setFinancialAssessment(prev => ({
                    ...prev,
                    ...reviewData
                  }));

                  // Clean review data to remove File objects before saving
                  const cleanedReviewData = cleanDataForFirestore(reviewData);
                  console.log('Cleaned review data:', cleanedReviewData);

                  // Save financial comments
                  await saveFinancialComments(applicationId, {
                    comments: reviewData.comments || reviewComments,
                    decision: reviewData.status || reviewDecision,
                    assessmentData: cleanedReviewData,
                    userId: user.uid,
                    userName: user.profile?.name || user.email || "Financial Team"
                  });

                  // Route decisions through the correct flow
                  if (reviewData.status) {
                    const decision = (reviewData.status || '').toLowerCase();
                    console.log('Handling financial decision via gated flow:', decision);
                    if (decision === 'approved' || decision === 'rejected') {
                      // Enforce dispute gating and proper workflow updates
                      const result = await completeFinancialVerification(applicationId, user.uid, {
                        status: decision,
                        comments: reviewData.comments || reviewComments,
                        assessment: cleanedReviewData,
                        reviewerId: user.uid,
                        reviewerName: user.profile?.name || user.email,
                        completedAt: new Date(),
                        processingStartTime: reviewStartTime || new Date(),
                        processingTime: Math.floor(elapsedTime / 60)
                      });
                      if (!result?.success) {
                        const errMsg = result?.error || 'Failed to complete financial verification';
                        alert(errMsg);
                        // Do not proceed to reload on failure
                        return;
                      }
                    } else {
                      // For drafts or pending_info etc., allow lightweight nested update
                      const updateResult = await updateFinancialStatus(applicationId, {
                        status: decision,
                        verifiedBy: user.uid,
                        verifiedAt: new Date(),
                        comments: reviewData.comments || reviewComments
                      });
                      if (!updateResult?.success) {
                        alert(updateResult?.error || 'Failed to update status');
                        return;
                      }
                    }
                    setReviewDecision(decision);
                    setReviewComments(reviewData.comments || '');
                  }

                  // Reload application data
                  console.log('Reloading application data...');
                  await loadApplicationData();

                  // Clear auto-saved data on successful submission
                  localStorage.removeItem(`financial_review_${applicationId}`);
                  setFinancialAssessment(prev => ({ ...prev, lastSavedAt: null }));

                  console.log('Financial review submitted successfully');
                  
                  // Switch back to application view and restore previous scroll for that tab
                  changeTab("application");
                  
                  // Show success message (you can add a toast notification here)
                  alert(`Financial review ${reviewData.status} successfully!`);
                } catch (error) {
                  console.error('Error submitting financial review:', error);
                  alert('Error submitting financial review. Please try again.');
                }
              }}
              onClose={() => changeTab("application")}
              autoSaveData={financialAssessment}
              onAutoSave={async (data, isManualSave = false) => {
                try {
                  // Save to localStorage for offline backup
                  const key = `financial_review_${applicationId}`;
                  localStorage.setItem(key, JSON.stringify({
                    ...data,
                    savedAt: new Date().toISOString()
                  }));
                  
                  // Update local state
                  setFinancialAssessment(prev => ({
                    ...prev,
                    lastSavedAt: new Date().toISOString()
                  }));

                  // Save to Firestore using saveFinancialComments
                  const savePayload = {
                    assessmentData: data,
                    lastUpdatedBy: user?.uid,
                    lastUpdatedAt: new Date().toISOString(),
                    userName: user?.profile?.name || user?.email || "Financial Team"
                  };

                  // Set appropriate save flag
                  if (isManualSave) {
                    savePayload.manualSave = true;
                  } else {
                    savePayload.autoSaved = true;
                  }

                  const result = await saveFinancialComments(applicationId, savePayload);

                  if (result?.success) {
                    console.log(`Draft ${isManualSave ? 'manually' : 'auto'}-saved to Firestore successfully`);
                  } else {
                    console.warn('Failed to save draft to Firestore:', result?.error);
                    throw new Error(result?.error || 'Failed to save draft');
                  }
                } catch (error) {
                  console.error('Error saving draft:', error);
                  throw error;
                }
              }}
              onRequestInfo={async (clarificationData) => {
                try {
                  // Save current form data before requesting info
                  if (financialAssessment) {
                    const key = `financial_review_${applicationId}`;
                    localStorage.setItem(key, JSON.stringify({
                      ...financialAssessment,
                      savedAt: new Date().toISOString()
                    }));
                  }

                  setHasPendingClarification(true);
                  setClarificationDetails(clarificationData);

                  // Show discussion panel on the right side
                  setShowDiscussionPanel(true);

                  // Send initial clarification message using discussion service
                  if (clarificationData && clarificationData.message && clarificationData.subject) {
                    await sendDiscussionMessage(applicationId, {
                      message: `[CLARIFICATION REQUEST] ${clarificationData.subject}\n\n${clarificationData.message}`,
                      type: 'clarification_request',
                      senderRole: 'financial',
                      senderName: user.profile?.name || user.email,
                      timestamp: new Date(),
                      metadata: {
                        requestType: 'clarification',
                        subject: clarificationData.subject
                      }
                    });

                    // Reload discussions
                    await loadDiscussions();
                  }

                  console.log('Clarification request sent');
                } catch (error) {
                  console.error('Error requesting information:', error);
                }
              }}
              hasPendingClarification={hasPendingClarification}
              clarificationDetails={clarificationDetails}
            />
          </div>
        </div>
      </div>

      {/* Right Side - Application Overview & Documents (40%) */}
      <div ref={rightPanelRef} className="lg:col-span-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 pl-2" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        <ApplicationRightPanel
          // Document panel props
          showDocumentPanel={showDocumentPanel}
          selectedDocument={selectedDocument}
          onCloseDocumentPanel={() => {
            setShowDocumentPanel(false);
            setSelectedDocument(null);
            // Restore previous scroll position to avoid jump-to-top
            setTimeout(() => {
              window.scrollTo(0, scrollYBeforeDocRef.current || 0);
              if (rightPanelRef?.current) rightPanelRef.current.scrollTop = rightPanelScrollRef.current || 0;
            }, 0);
          }}
          onOpenDocument={(document) => {
            scrollYBeforeDocRef.current = window.scrollY;
            if (rightPanelRef?.current) rightPanelScrollRef.current = rightPanelRef.current.scrollTop || 0;
            setSelectedDocument(document);
            setShowDocumentPanel(true);
          }}

          // Discussion panel props
          showDiscussionPanel={showDiscussionPanel}
          onCloseDiscussionPanel={() => setShowDiscussionPanel(false)}
          onOpenDiscussionPanel={() => setShowDiscussionPanel(true)}
          applicationId={applicationId}
          onUnreadCountChange={handleUnreadCountChange}
          hasPendingClarification={hasPendingClarification}
          unreadCount={unreadCount}

          // Application data
          application={financialApprovedEffective ? {
            ...application,
            status: application.status || 'financial_approved',
            workflow: {
              ...(application?.workflow || {}),
              financialVerification: {
                ...(application?.workflow?.financialVerification || {}),
                status: application?.workflow?.financialVerification?.status || 'approved'
              }
            }
          } : application}

          // Collapsible sections
          collapsedSections={collapsedSections}
          onToggleSection={(sectionKey) => {
            setCollapsedSections(prev => ({
              ...prev,
              [sectionKey]: !prev[sectionKey]
            }));
          }}

          // Utility functions
          formatAmount={formatAmount}
          formatDate={formatDate}

          // Document query props
          documentQueries={documentQueries}
          onOpenQueryModal={openQueryModal}
          userTeam="finance"
        />
      </div>
    </div>
  );

  // Derived header status: keep showing approved when we've observed it once, unless challenged/reassigned to finance
  const hdrKypReassign = application?.workflow?.medicalVerification?.kypReassignment || null;
  const hdrReassignedToFinance = (
    hdrKypReassign && (
      hdrKypReassign.target === 'finance' || (hdrKypReassign.targets || [])?.includes?.('finance')
    )
  );
  const hdrReassignStatus = hdrKypReassign?.status || 'pending';
  const hdrIsReassignmentActiveForFinance = hdrReassignedToFinance && !['finance_completed', 'completed'].includes(hdrReassignStatus);
  const hdrIsChallengedForFinance = (
    application?.workflow?.medicalVerification?.kypHospitalAcknowledgement?.status === 'challenged' &&
    hdrIsReassignmentActiveForFinance
  );
  const statusForBadge = financialApprovedEffective && !hdrIsChallengedForFinance
    ? 'financial_approved'
    : application?.status;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 relative">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button id="financial-back-button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    const message = "Go back to All Applications? Unsaved changes may be lost.";
                    const confirmed = window.confirm(message);
                    if (confirmed) {
                      navigate("/finance/dashboard?section=all-applications");
                    }
                  } catch (err) {
                    navigate("/finance/dashboard?section=all-applications");
                  }
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </button>

              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Financial Review
                </h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>ID: {application.applicationId}</span>
                  <span>•</span>
                  <span>Patient: {application.patient?.name || application.patientDetails?.name}</span>
                  <span>•</span>
                  <span>Hospital: {application.hospitalName || application.hospital?.name}</span>
                  <span>•</span>
                  <span>Submitted: {formatDate(application.submittedAt)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Global Search Bar */}
              <div className="w-80 hidden lg:block">
                <GlobalSearchBar userRole="finance" />
              </div>

              {isTimerActive && (
                <div className="flex items-center space-x-2 bg-green-50 px-3 py-1 rounded-lg">
                  <ClockIcon className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">
                    {formatTime(elapsedTime)}
                  </span>
                </div>
              )}

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(
                  statusForBadge
                )}`}
              >
                {statusForBadge?.replace("_", " ").charAt(0).toUpperCase() +
                  statusForBadge?.replace("_", " ").slice(1)}
              </span>

              {application.priority && application.priority !== "normal" && (
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${application.priority === "urgent"
                      ? "bg-red-100 text-red-800"
                      : "bg-yellow-100 text-yellow-800"
                    }`}
                >
                  {application.priority.charAt(0).toUpperCase() +
                    application.priority.slice(1)}
                </span>
              )}

              {application.emergency && (
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                  Emergency
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => changeTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm relative ${activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full ${tab.alert ? "text-red-100 bg-red-600" : "text-blue-100 bg-blue-600"
                      }`}>
                      {tab.count}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Financial tab uses full-width grid layout like PolicyApplicationDetails */}
        {activeTab === "financial" ? (
          renderEnhancedFinancialReview()
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Main Content Area */}
            <div className="lg:col-span-3">
              {activeTab === "application" && renderApplicationOverview()}
              {activeTab === "documents" && renderDocumentVerification()}
              {activeTab === "credit" && renderCreditAnalysis()}
              {activeTab === "disputes" && (
                <DisputeManager
                  applicationId={applicationId}
                  applicationData={application}
                  onDisputeUpdate={checkDisputeStatus}
                  forceDepartment="financial"
                />
              )}
              {activeTab === "discussions" && renderDiscussions()}
              {activeTab === "sanctions" && renderSanctions()}
              {activeTab === "status" && renderStatusTimeline()}
            </div>

            {/* Right Panel - Application Overview & Documents */}
            <div ref={rightPanelRef} className="lg:col-span-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              <ApplicationRightPanel
                // Document panel props
                showDocumentPanel={showDocumentPanel}
                selectedDocument={selectedDocument}
                onCloseDocumentPanel={() => {
                  setShowDocumentPanel(false);
                  setSelectedDocument(null);
                  setTimeout(() => {
                    window.scrollTo(0, scrollYBeforeDocRef.current || 0);
                    if (rightPanelRef?.current) rightPanelRef.current.scrollTop = rightPanelScrollRef.current || 0;
                  }, 0);
                }}
                onOpenDocument={(document) => {
                  scrollYBeforeDocRef.current = window.scrollY;
                  if (rightPanelRef?.current) rightPanelScrollRef.current = rightPanelRef.current.scrollTop || 0;
                  setSelectedDocument(document);
                  setShowDocumentPanel(true);
                }}

                // Discussion panel props
                showDiscussionPanel={showDiscussionPanel}
                onCloseDiscussionPanel={() => setShowDiscussionPanel(false)}
                onOpenDiscussionPanel={() => setShowDiscussionPanel(true)}
                applicationId={applicationId}
                onUnreadCountChange={handleUnreadCountChange}
                hasPendingClarification={hasPendingClarification}
                unreadCount={unreadCount}

                // Application data
                application={financialApprovedEffective ? {
                  ...application,
                  status: application.status || 'financial_approved',
                  workflow: {
                    ...(application?.workflow || {}),
                    financialVerification: {
                      ...(application?.workflow?.financialVerification || {}),
                      status: application?.workflow?.financialVerification?.status || 'approved'
                    }
                  }
                } : application}

                // Collapsible sections
                collapsedSections={collapsedSections}
                onToggleSection={(sectionKey) => {
                  setCollapsedSections(prev => ({
                    ...prev,
                    [sectionKey]: !prev[sectionKey]
                  }));
                }}

                // Utility functions
                formatAmount={formatAmount}
                formatDate={formatDate}
              />
            </div>
          </div>
        )}
      </div>

      {/* Letter Viewer Modal */}
      {showLetterModal && selectedLetter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-60" onClick={() => {
            // Clean up blob URL if it was created for preview
            if (selectedLetter._isPreviewUrl && selectedLetter.url) {
              URL.revokeObjectURL(selectedLetter.url);
            }
            setShowLetterModal(false);
          }} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-6xl w-[92vw] mx-auto border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
              <div className="min-w-0 pr-3 flex items-center space-x-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{selectedLetter.title}</h3>
                  <p className="text-sm text-gray-600">Generated on {formatDate(selectedLetter.generatedAt)}</p>
                </div>
                {/* Consistent compact controls */}
                {(selectedLetter.url) && (
                  <div className="hidden md:flex items-center space-x-1 text-gray-600">
                    <span className="text-xs">Zoom</span>
                    <button onClick={() => setLetterZoom(z => Math.max(0.25, Math.round((z - 0.1) * 100) / 100))} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100">-</button>
                    <span className="w-12 text-center text-xs">{Math.round(letterZoom * 100)}%</span>
                    <button onClick={() => setLetterZoom(z => Math.min(4, Math.round((z + 0.1) * 100) / 100))} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100">+</button>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {(selectedLetter.url && (/(\.pdf)(\?|$)/i.test(selectedLetter.fileName || selectedLetter.title || '') || selectedLetter.type === 'kyp' || selectedLetter.type === 'approval')) && (
                  <div className="flex items-center space-x-1 mr-2">
                    <button onClick={() => setLetterZoom(z => Math.max(0.25, Math.round((z - 0.1) * 100) / 100))} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100">-</button>
                    <span className="w-12 text-center text-xs">{Math.round(letterZoom * 100)}%</span>
                    <button onClick={() => setLetterZoom(z => Math.min(4, Math.round((z + 0.1) * 100) / 100))} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100">+</button>
                    <button onClick={() => setLetterZoom(1)} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100">Fit</button>
                  </div>
                )}
                {selectedLetter.url && (
                  <button
                    onClick={() => window.open(selectedLetter.url, '_blank')}
                    className="px-3 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 flex items-center space-x-2 text-sm"
                  >
                    <span>Open</span>
                  </button>
                )}
                <button
                  onClick={() => handleLetterDownload(selectedLetter)}
                  className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 flex items-center space-x-2 text-sm"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </button>
                <button 
                  onClick={() => {
                    // Clean up blob URL if it was created for preview
                    if (selectedLetter._isPreviewUrl && selectedLetter.url) {
                      URL.revokeObjectURL(selectedLetter.url);
                    }
                    setShowLetterModal(false);
                  }} 
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="w-full h-[80vh] border border-gray-200 rounded overflow-hidden bg-gray-50">
                {selectedLetter.url ? (
                  <>
                    <iframe
                      src={`${selectedLetter.url}#toolbar=0&navpanes=0&scrollbar=0&zoom=${Math.round(letterZoom*100)}`}
                      className="w-full h-full"
                      title="Letter Viewer"
                      onError={() => {
                        // Fallback for iframe errors
                        console.log('Iframe error, trying alternative method');
                      }}
                    />
                    {/* Fallback link if iframe fails */}
                    <div className="absolute bottom-4 right-4">
                      <a
                        href={selectedLetter.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 flex items-center space-x-1"
                      >
                        <Eye className="h-3 w-3" />
                        <span>Open in New Tab</span>
                      </a>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">Letter preview not available</p>
                      <p className="text-sm text-gray-500 mt-2">Click download to view the letter</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAL Preview Modal (React-PDF) */}
      {showFALPreview && application && (
        <FALPreviewModal
          isOpen={showFALPreview}
          onClose={() => setShowFALPreview(false)}
          application={application}
          remarks={application?.workflow?.dischargeSettlement?.comments || ''}
          canDownload={true}
          template="comprehensive"
        />
      )}
    </div>
  );

  // Application Overview
  function renderApplicationOverview() {
    return (
      <div className="space-y-6">
        {/* Patient Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-teal-100 p-2 rounded-lg">
                <User className="h-5 w-5 text-teal-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Patient Information</h2>
            </div>
            <button
              onClick={() => toggleSection('patient')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              {collapsedSections.patient ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          </div>

          {!collapsedSections.patient && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-500">Patient Name</label>
                <p className="text-lg font-semibold text-gray-900">
                  {application.patient?.name || application.patientDetails?.name}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Age & Gender</label>
                <p className="text-gray-900">
                  {application.patient?.age || application.patientDetails?.age} years, {application.patient?.gender || application.patientDetails?.gender}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Contact</label>
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-3 w-3 text-gray-400" />
                    <p className="text-sm text-gray-900">
                      {application.patient?.phone || application.patientDetails?.phone}
                    </p>
                  </div>
                  {(application.patient?.email || application.patientDetails?.email) && (
                    <div className="flex items-center space-x-2">
                      <Mail className="h-3 w-3 text-gray-400" />
                      <p className="text-sm text-gray-900">
                        {application.patient?.email || application.patientDetails?.email}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Address</label>
                <div className="flex items-start space-x-2">
                  <MapPin className="h-3 w-3 text-gray-400 mt-1" />
                  <p className="text-sm text-gray-900">
                    {application.patient?.address || application.patientDetails?.address}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Medical Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Stethoscope className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Medical Information</h2>
            </div>
            <button
              onClick={() => toggleSection('medical')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              {collapsedSections.medical ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          </div>

          {!collapsedSections.medical && (
            <div className="space-y-6">
              {/* Diagnosis Update */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div className="bg-red-100 p-1 rounded">
                      <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-red-800">Diagnosis Update</h4>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                    {(() => {
                      const medicalStatus = application?.workflow?.medicalVerification?.status;
                      const approvedForProcess = application?.workflow?.medicalVerification?.approvedForProcess;
                      const applicationStatus = application?.status;
                      const medicalApproval = application?.medical?.approval?.status;
                      const doctorApproval = application?.medical?.doctorApproval;
                      const doctorReviewStatus = application?.workflow?.doctorReview?.status;
                      const verifiedAt = application?.workflow?.medicalVerification?.verifiedAt;
                      
                      // Check multiple conditions for approval - comprehensive list
                      const isApproved = 
                        medicalStatus === 'approved' || 
                        medicalStatus === 'medical_approved' ||
                        approvedForProcess === true ||
                        applicationStatus === 'approved_for_process' ||
                        applicationStatus === 'medical_approved' ||
                        applicationStatus === 'approved' ||
                        medicalApproval === 'approved' ||
                        medicalApproval === 'medical_approved' ||
                        doctorApproval === true ||
                        doctorReviewStatus === 'approved' ||
                        doctorReviewStatus === 'medical_approved' ||
                        Boolean(verifiedAt);
                      
                      if (isApproved) {
                        return (
                          <span className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">
                            APPROVED
                          </span>
                        );
                      } else {
                        return (
                          <span className="text-sm bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-semibold">
                            PENDING
                          </span>
                        );
                      }
                    })()}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Condition:</span>
                    <span className="text-sm font-semibold text-red-700">ACUTE GASTRITIS WITH VERTIGO-DEHYDRATION</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">ICD Code:</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">K29.00</span>
                    <span className="text-sm text-gray-600">- Acute gastritis without bleeding</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Type:</span>
                    <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">medical</span>
                  </div>
                </div>
              </div>

              {/* Treatment Details */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="bg-green-100 p-1 rounded">
                    <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-green-800">Treatment Details</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Procedure:</span>
                    <span className="text-sm font-semibold text-green-700">MEDICAL MANAGEMENT</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Last reviewed by:</span>
                    <span className="text-sm font-semibold text-gray-800">Muni Bhargavi</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Review Date:</span>
                    <span className="text-sm text-gray-600">10/16/2025</span>
                  </div>
                </div>
              </div>

              {/* Additional Medical Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Estimated Cost</label>
                  <p className="text-lg font-semibold text-teal-600">
                    {formatAmount(application.medical?.estimatedCost || application.requestedLoanAmount)}
                  </p>
                </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Hospital</label>
                    <p className="text-gray-900">{application.hospitalName}</p>
                  </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Consulting Doctor</label>
                  <p className="text-gray-900">{application.medical?.doctor?.name}</p>
                  <p className="text-sm text-gray-600">{application.medical?.doctor?.designation}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Admission</label>
                    <p className="text-sm text-gray-900">{application.medical?.admissionDate}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Discharge</label>
                    <p className="text-sm text-gray-900">{application.medical?.dischargeDate || "TBD"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Policy Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <Shield className="h-5 w-5 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Insurance Policy</h2>
            </div>
            <button
              onClick={() => toggleSection('policy')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              {collapsedSections.policy ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          </div>

          {!collapsedSections.policy && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-500">Policy Number</label>
                <p className="text-gray-900 font-mono">{application.policy?.number}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Insurance Company</label>
                <p className="text-gray-900">{application.policy?.insuranceCompany}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Coverage Amount</label>
                <p className="text-lg font-semibold text-gray-900">
                  {formatAmount(application.policy?.coverageAmount)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Financial Assessment - Enhanced Form Design matching Policy Review
  function renderFinancialAssessment() {
    return (
      <div className="space-y-6">
        {/* Medical Information Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded-lg">
                <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Medical Information</h3>
            </div>
          </div>

          <div className="space-y-6">
            {/* Diagnosis Update */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <div className="bg-red-100 p-1 rounded">
                  <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-red-800">Diagnosis Update</h4>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Condition:</span>
                  <span className="text-sm font-semibold text-red-700">ACUTE GASTRITIS WITH VERTIGO-DEHYDRATION</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">ICD Code:</span>
                  <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">K29.00</span>
                  <span className="text-sm text-gray-600">- Acute gastritis without bleeding</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Type:</span>
                  <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">medical</span>
                </div>
              </div>
            </div>

            {/* Treatment Details */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <div className="bg-green-100 p-1 rounded">
                  <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <h4 className="font-semibold text-green-800">Treatment Details</h4>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Procedure:</span>
                  <span className="text-sm font-semibold text-green-700">MEDICAL MANAGEMENT</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Last reviewed by:</span>
                  <span className="text-sm font-semibold text-gray-800">Muni Bhargavi</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Review Date:</span>
                  <span className="text-sm text-gray-600">10/16/2025</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Assessment Tools</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => handleFetchCreditScore(application)}
              disabled={actionLoading.creditScore}
              className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {actionLoading.creditScore ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <CreditCardIcon className="h-4 w-4" />
              )}
              <span>Fetch Credit Score</span>
            </button>

            <button
              onClick={handleVerifyDocuments}
              disabled={actionLoading.documents}
              className="flex items-center justify-center space-x-2 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {actionLoading.documents ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <FileCheckIcon className="h-4 w-4" />
              )}
              <span>Verify Documents</span>
            </button>

            <button
              onClick={() => calculateRiskAssessment(financialAssessment.creditScore)}
              disabled={actionLoading.riskAssessment || !financialAssessment.creditScore}
              className="flex items-center justify-center space-x-2 bg-orange-600 text-white px-4 py-3 rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {actionLoading.riskAssessment ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Calculator className="h-4 w-4" />
              )}
              <span>Risk Assessment</span>
            </button>
          </div>
        </div>

        {/* Personal Details Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Personal Details</h3>
            </div>
          </div>

          <div className="space-y-6">
            {/* Patient and Borrower Same */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isPatientBorrowerSame"
                checked={financialAssessment.isPatientBorrowerSame}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  isPatientBorrowerSame: e.target.checked,
                  borrowerName: e.target.checked ? prev.patientName : prev.borrowerName
                }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isPatientBorrowerSame" className="text-sm font-medium text-gray-700">
                Is patient and borrower are the same?
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name of the Patient *</label>
                <input
                  type="text"
                  value={financialAssessment.patientName}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    patientName: e.target.value,
                    borrowerName: prev.isPatientBorrowerSame ? e.target.value : prev.borrowerName
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Patient name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name of the Borrower *</label>
                <input
                  type="text"
                  value={financialAssessment.borrowerName}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    borrowerName: e.target.value
                  }))}
                  disabled={financialAssessment.isPatientBorrowerSame}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  placeholder="Full Name of the Borrower"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
                <input
                  type="tel"
                  value={financialAssessment.contactNumber}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    contactNumber: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Contact Number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email ID *</label>
                <input
                  type="email"
                  value={financialAssessment.emailId}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    emailId: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Email ID"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Aadhar Number *</label>
                <input
                  type="text"
                  value={financialAssessment.aadharNumber}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    aadharNumber: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Aadhaar Number"
                  maxLength="12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PAN Number *</label>
                <input
                  type="text"
                  value={financialAssessment.panNumber}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    panNumber: e.target.value.toUpperCase()
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="PAN Number"
                  maxLength="10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Income (₹) *</label>
                <input
                  type="number"
                  value={financialAssessment.loanDetails.totalMonthlyIncome || ''}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    loanDetails: { ...prev.loanDetails, totalMonthlyIncome: parseFloat(e.target.value) || 0 }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Total Income"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Loan Scheme (Months)</label>
                <select
                  value={financialAssessment.loanDetails.schemeMonths}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    loanDetails: { ...prev.loanDetails, schemeMonths: parseInt(e.target.value) }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="6">6 Months</option>
                  <option value="12">12 Months</option>
                  <option value="18">18 Months</option>
                  <option value="24">24 Months</option>
                  <option value="36">36 Months</option>
                  <option value="48">48 Months</option>
                  <option value="60">60 Months</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Interest/Subvention %</label>
                <input
                  type="number"
                  step="0.1"
                  value={financialAssessment.loanDetails.interestSubventionPercent}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    loanDetails: { ...prev.loanDetails, interestSubventionPercent: parseFloat(e.target.value) || 0 }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Address Details Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <MapPin className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Address Details</h3>
            </div>
          </div>

          <div className="space-y-6">
            {/* Current Address */}
            <div>
              <h4 className="font-medium text-gray-900 mb-4">Current Address</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Complete Address *</label>
                  <textarea
                    value={financialAssessment.currentAddress.completeAddress}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      currentAddress: { ...prev.currentAddress, completeAddress: e.target.value }
                    }))}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter complete current address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pincode *</label>
                  <input
                    type="text"
                    value={financialAssessment.currentAddress.pincode}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      currentAddress: { ...prev.currentAddress, pincode: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Pincode"
                    maxLength="6"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                  <input
                    type="text"
                    value={financialAssessment.currentAddress.city}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      currentAddress: { ...prev.currentAddress, city: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="City"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                  <input
                    type="text"
                    value={financialAssessment.currentAddress.state}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      currentAddress: { ...prev.currentAddress, state: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="State"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                  <input
                    type="text"
                    value={financialAssessment.currentAddress.country}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      currentAddress: { ...prev.currentAddress, country: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Country"
                  />
                </div>
              </div>
            </div>

            {/* Permanent Address Same as Current */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isSameAddress"
                checked={financialAssessment.isSameAddress}
                onChange={(e) => {
                  const isSame = e.target.checked;
                  setFinancialAssessment(prev => ({
                    ...prev,
                    isSameAddress: isSame,
                    permanentAddress: isSame ? { ...prev.currentAddress } : prev.permanentAddress
                  }));
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isSameAddress" className="text-sm font-medium text-gray-700">
                Permanent address is same as current address
              </label>
            </div>

            {/* Permanent Address */}
            {!financialAssessment.isSameAddress && (
              <div>
                <h4 className="font-medium text-gray-900 mb-4">Permanent Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Complete Address *</label>
                    <textarea
                      value={financialAssessment.permanentAddress.completeAddress}
                      onChange={(e) => setFinancialAssessment(prev => ({
                        ...prev,
                        permanentAddress: { ...prev.permanentAddress, completeAddress: e.target.value }
                      }))}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter complete permanent address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pincode *</label>
                    <input
                      type="text"
                      value={financialAssessment.permanentAddress.pincode}
                      onChange={(e) => setFinancialAssessment(prev => ({
                        ...prev,
                        permanentAddress: { ...prev.permanentAddress, pincode: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Pincode"
                      maxLength="6"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                    <input
                      type="text"
                      value={financialAssessment.permanentAddress.city}
                      onChange={(e) => setFinancialAssessment(prev => ({
                        ...prev,
                        permanentAddress: { ...prev.permanentAddress, city: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="City"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                    <input
                      type="text"
                      value={financialAssessment.permanentAddress.state}
                      onChange={(e) => setFinancialAssessment(prev => ({
                        ...prev,
                        permanentAddress: { ...prev.permanentAddress, state: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="State"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                    <input
                      type="text"
                      value={financialAssessment.permanentAddress.country}
                      onChange={(e) => setFinancialAssessment(prev => ({
                        ...prev,
                        permanentAddress: { ...prev.permanentAddress, country: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Country"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Reference Details Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-yellow-100 p-2 rounded-lg">
                <Users className="h-5 w-5 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Reference Details</h3>
            </div>
          </div>

          <div className="space-y-6">
            {/* Reference 1 */}
            <div>
              <h4 className="font-medium text-gray-900 mb-4">Reference 1</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={financialAssessment.references.reference1.fullName}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      references: {
                        ...prev.references,
                        reference1: { ...prev.references.reference1, fullName: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Reference 1 Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
                  <input
                    type="tel"
                    value={financialAssessment.references.reference1.contactNumber}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      references: {
                        ...prev.references,
                        reference1: { ...prev.references.reference1, contactNumber: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Contact Number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Relationship *</label>
                  <input
                    type="text"
                    value={financialAssessment.references.reference1.relationship}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      references: {
                        ...prev.references,
                        reference1: { ...prev.references.reference1, relationship: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Relationship"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <input
                    type="text"
                    value={financialAssessment.references.reference1.address}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      references: {
                        ...prev.references,
                        reference1: { ...prev.references.reference1, address: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Address"
                  />
                </div>
              </div>
            </div>

            {/* Reference 2 */}
            <div>
              <h4 className="font-medium text-gray-900 mb-4">Reference 2</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={financialAssessment.references.reference2.fullName}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      references: {
                        ...prev.references,
                        reference2: { ...prev.references.reference2, fullName: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Reference 2 Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
                  <input
                    type="tel"
                    value={financialAssessment.references.reference2.contactNumber}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      references: {
                        ...prev.references,
                        reference2: { ...prev.references.reference2, contactNumber: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Contact Number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Relationship *</label>
                  <input
                    type="text"
                    value={financialAssessment.references.reference2.relationship}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      references: {
                        ...prev.references,
                        reference2: { ...prev.references.reference2, relationship: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Relationship"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <input
                    type="text"
                    value={financialAssessment.references.reference2.address}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      references: {
                        ...prev.references,
                        reference2: { ...prev.references.reference2, address: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Address"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hospital Information Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded-lg">
                <Building className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Hospital Information</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hospital Name *</label>
              <input
                type="text"
                value={financialAssessment.hospitalInformation.hospitalName}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  hospitalInformation: { ...prev.hospitalInformation, hospitalName: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Hospital Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Doctor Name *</label>
              <input
                type="text"
                value={financialAssessment.hospitalInformation.doctorName}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  hospitalInformation: { ...prev.hospitalInformation, doctorName: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Doctor Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Treatment Cost (₹) *</label>
              <input
                type="number"
                value={financialAssessment.hospitalInformation.treatmentCost || ''}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  hospitalInformation: { ...prev.hospitalInformation, treatmentCost: parseFloat(e.target.value) || 0 }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Treatment Cost"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Hospital Address</label>
              <textarea
                value={financialAssessment.hospitalInformation.hospitalAddress}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  hospitalInformation: { ...prev.hospitalInformation, hospitalAddress: e.target.value }
                }))}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Hospital Address"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Treatment Details</label>
              <textarea
                value={financialAssessment.hospitalInformation.treatmentDetails}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  hospitalInformation: { ...prev.hospitalInformation, treatmentDetails: e.target.value }
                }))}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Detailed treatment information"
              />
            </div>
          </div>
        </div>

        {/* Bank Details Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <Bank className="h-5 w-5 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Bank Details</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name *</label>
              <input
                type="text"
                value={financialAssessment.bankDetails.bankName}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  bankDetails: { ...prev.bankDetails, bankName: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Bank Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Number *</label>
              <input
                type="text"
                value={financialAssessment.bankDetails.accountNumber}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  bankDetails: { ...prev.bankDetails, accountNumber: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Account Number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code *</label>
              <input
                type="text"
                value={financialAssessment.bankDetails.ifscCode}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  bankDetails: { ...prev.bankDetails, ifscCode: e.target.value.toUpperCase() }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="IFSC Code"
                maxLength="11"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder Name *</label>
              <input
                type="text"
                value={financialAssessment.bankDetails.accountHolderName}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  bankDetails: { ...prev.bankDetails, accountHolderName: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Account Holder Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Type</label>
              <select
                value={financialAssessment.bankDetails.accountType}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  bankDetails: { ...prev.bankDetails, accountType: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Savings">Savings Account</option>
                <option value="Current">Current Account</option>
                <option value="Salary">Salary Account</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Branch Name</label>
              <input
                type="text"
                value={financialAssessment.bankDetails.branchName}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  bankDetails: { ...prev.bankDetails, branchName: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Branch Name"
              />
            </div>
          </div>
        </div>

        {/* Document Status Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <FileCheckIcon className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Document Upload & Verification Status</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(financialAssessment.documentStatus).map(([docKey, docStatus]) => (
              <div key={docKey} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 capitalize">
                    {docKey.replace(/([A-Z])/g, ' $1').trim()}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <div className={`p-1 rounded-full ${docStatus.uploaded ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <Upload className={`h-3 w-3 ${docStatus.uploaded ? 'text-blue-600' : 'text-gray-400'}`} />
                    </div>
                    <div className={`p-1 rounded-full ${docStatus.verified ? 'bg-green-100' : 'bg-red-100'}`}>
                      {docStatus.verified ? (
                        <CheckCircle className="h-3 w-3 text-green-600" />
                      ) : (
                        <XCircle className="h-3 w-3 text-red-600" />
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Uploaded:</span>
                    <span className={`text-xs font-medium ${docStatus.uploaded ? 'text-blue-600' : 'text-gray-400'}`}>
                      {docStatus.uploaded ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Verified:</span>
                    <span className={`text-xs font-medium ${docStatus.verified ? 'text-green-600' : 'text-red-600'}`}>
                      {docStatus.verified ? 'Yes' : 'No'}
                    </span>
                  </div>

                  <label className="flex items-center space-x-2 mt-2">
                    <input
                      type="checkbox"
                      checked={docStatus.verified}
                      onChange={(e) => setFinancialAssessment(prev => ({
                        ...prev,
                        documentStatus: {
                          ...prev.documentStatus,
                          [docKey]: { ...docStatus, verified: e.target.checked }
                        }
                      }))}
                      className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-xs text-gray-700">Mark as verified</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Credit Information Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <CreditCardIcon className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Credit Information</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Credit Score *</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="300"
                  max="900"
                  value={financialAssessment.creditScore || ''}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    creditScore: parseInt(e.target.value) || null
                  }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter CIBIL score"
                />
                <div className={`px-2 py-1 rounded text-xs font-medium ${financialAssessment.creditScore >= 750 ? 'bg-green-100 text-green-800' :
                    financialAssessment.creditScore >= 650 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                  }`}>
                  {financialAssessment.creditScore >= 750 ? 'Excellent' :
                    financialAssessment.creditScore >= 650 ? 'Good' : 'Poor'}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Credit Bureau</label>
              <select
                value={financialAssessment.creditBureau}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  creditBureau: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="CIBIL">CIBIL</option>
                <option value="Experian">Experian</option>
                <option value="Equifax">Equifax</option>
                <option value="CRIF">CRIF High Mark</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Loan ID (Internal Tracking)</label>
              <input
                type="text"
                value={financialAssessment.loanId}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  loanId: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter loan ID for tracking"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lender Name</label>
              <input
                type="text"
                value={financialAssessment.lenderName}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  lenderName: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter lender name"
              />
            </div>
          </div>
        </div>

        {/* KYC Verification Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <ShieldCheck className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">KYC Verification Status</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { key: 'aadharVerified', label: 'Aadhar Verified', icon: Fingerprint },
              { key: 'panVerified', label: 'PAN Verified', icon: CreditCardIcon },
              { key: 'bankAccountVerified', label: 'Bank Account Verified', icon: Bank },
              { key: 'addressVerified', label: 'Address Verified', icon: MapPin },
              { key: 'incomeVerified', label: 'Income Verified', icon: CircleDollarSign },
              { key: 'employmentVerified', label: 'Employment Verified', icon: Building },
              { key: 'documentsAuthentic', label: 'Documents Authentic', icon: FileCheckIcon }
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <item.icon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">{item.label}</span>
                </div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={financialAssessment[item.key]}
                    onChange={(e) => setFinancialAssessment(prev => ({
                      ...prev,
                      [item.key]: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className={`text-sm font-medium ${financialAssessment[item.key] ? 'text-green-600' : 'text-gray-400'
                    }`}>
                    {financialAssessment[item.key] ? 'Verified' : 'Pending'}
                  </span>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Internal Assessment Notes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-gray-100 p-2 rounded-lg">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Internal Assessment Notes</h3>
            </div>
          </div>

          <textarea
            value={financialAssessment.internalNotes}
            onChange={(e) => setFinancialAssessment(prev => ({
              ...prev,
              internalNotes: e.target.value
            }))}
            rows="6"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Add detailed notes about the financial assessment, risk factors, special conditions, or any other relevant information..."
          />
        </div>

        {/* Save Button */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-end space-x-4">
            <button
              onClick={handleSaveInternalComments}
              disabled={commentsSaving}
              className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {commentsSaving ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>Save Assessment</span>
            </button>
          </div>
        </div>

        {/* Loan Details & Internal Tracking */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Database className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Loan Details & Internal Tracking</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Loan ID</label>
              <input
                type="text"
                value={financialAssessment.loanId}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  loanId: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter loan ID for tracking"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lender Name</label>
              <input
                type="text"
                value={financialAssessment.lenderName}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  lenderName: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter lender name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Loan Type</label>
              <select
                value={financialAssessment.loanType}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  loanType: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="medical">Medical Loan</option>
                <option value="personal">Personal Loan</option>
                <option value="emergency">Emergency Loan</option>
                <option value="healthcare">Healthcare Loan</option>
              </select>
            </div>
          </div>
        </div>

        {/* Financial Assessment */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-yellow-100 p-2 rounded-lg">
                <TrendUp className="h-5 w-5 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Financial Assessment</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Income (₹)</label>
              <input
                type="number"
                value={financialAssessment.monthlyIncome || ''}
                onChange={(e) => {
                  const income = parseFloat(e.target.value) || 0;
                  const existingEMI = financialAssessment.existingEMI || 0;
                  const newEMI = financialAssessment.emi || 0;
                  const totalEMI = existingEMI + newEMI;
                  const foir = income > 0 ? (totalEMI / income * 100) : 0;

                  setFinancialAssessment(prev => ({
                    ...prev,
                    monthlyIncome: income,
                    foir: Math.round(foir * 100) / 100
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter monthly income"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Existing EMI (₹)</label>
              <input
                type="number"
                value={financialAssessment.existingEMI || ''}
                onChange={(e) => {
                  const existingEMI = parseFloat(e.target.value) || 0;
                  const income = financialAssessment.monthlyIncome || 0;
                  const newEMI = financialAssessment.emi || 0;
                  const totalEMI = existingEMI + newEMI;
                  const foir = income > 0 ? (totalEMI / income * 100) : 0;

                  setFinancialAssessment(prev => ({
                    ...prev,
                    existingEMI: existingEMI,
                    foir: Math.round(foir * 100) / 100
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter existing EMI"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">FOIR (%)</label>
              <div className="relative">
                <input
                  type="number"
                  value={financialAssessment.foir || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  placeholder="Auto-calculated"
                />
                <div className={`absolute right-2 top-2 px-2 py-1 rounded text-xs font-medium ${financialAssessment.foir <= 50 ? 'bg-green-100 text-green-800' :
                    financialAssessment.foir <= 70 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                  }`}>
                  {financialAssessment.foir <= 50 ? 'Good' :
                    financialAssessment.foir <= 70 ? 'Acceptable' : 'High Risk'}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Eligible Amount (₹)</label>
                <input
                type="number"
                value={financialAssessment.eligibleAmount || ''}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  eligibleAmount: parseFloat(e.target.value) || 0
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                readOnly={isReadOnly}
                placeholder="Enter eligible amount"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recommended Amount (₹)</label>
                <input
                type="number"
                value={financialAssessment.recommendedAmount || ''}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  recommendedAmount: parseFloat(e.target.value) || 0
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                readOnly={isReadOnly}
                placeholder="Enter recommended amount"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Approved Amount (₹)</label>
                <input
                type="number"
                value={financialAssessment.approvedAmount || ''}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  approvedAmount: parseFloat(e.target.value) || 0
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                readOnly={isReadOnly}
                placeholder="Enter approved amount"
              />
            </div>
          </div>
        </div>

        {/* Loan Terms */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <Percent className="h-5 w-5 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Loan Terms & Conditions</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Interest Rate (%)</label>
              <input
                type="number"
                step="0.1"
                value={financialAssessment.interestRate || ''}
                onChange={(e) => {
                  const rate = parseFloat(e.target.value) || 0;
                  const principal = financialAssessment.approvedAmount || 0;
                  const tenure = financialAssessment.tenure || 12;

                  // Calculate EMI using formula: P * r * (1+r)^n / ((1+r)^n - 1)
                  const monthlyRate = rate / 12 / 100;
                  const emi = principal > 0 && rate > 0 ?
                    (principal * monthlyRate * Math.pow(1 + monthlyRate, tenure)) /
                    (Math.pow(1 + monthlyRate, tenure) - 1) : 0;

                  setFinancialAssessment(prev => ({
                    ...prev,
                    interestRate: rate,
                    emi: Math.round(emi)
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter interest rate"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tenure (Months)</label>
              <select
                value={financialAssessment.tenure}
                onChange={(e) => {
                  const tenure = parseInt(e.target.value) || 12;
                  const principal = financialAssessment.approvedAmount || 0;
                  const rate = financialAssessment.interestRate || 0;

                  // Recalculate EMI
                  const monthlyRate = rate / 12 / 100;
                  const emi = principal > 0 && rate > 0 ?
                    (principal * monthlyRate * Math.pow(1 + monthlyRate, tenure)) /
                    (Math.pow(1 + monthlyRate, tenure) - 1) : 0;

                  setFinancialAssessment(prev => ({
                    ...prev,
                    tenure,
                    emi: Math.round(emi)
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="6">6 Months</option>
                <option value="12">12 Months</option>
                <option value="18">18 Months</option>
                <option value="24">24 Months</option>
                <option value="36">36 Months</option>
                <option value="48">48 Months</option>
                <option value="60">60 Months</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">EMI (₹)</label>
              <input
                type="number"
                value={financialAssessment.emi || ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                placeholder="Auto-calculated"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Risk Category</label>
              <select
                value={financialAssessment.riskCategory}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  riskCategory: e.target.value,
                  riskAssessment: e.target.value // for backward compatibility
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Risk Level</option>
                <option value="low">Low Risk</option>
                <option value="medium">Medium Risk</option>
                <option value="high">High Risk</option>
                <option value="very_high">Very High Risk</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Processing Fee</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  value={financialAssessment.processingFee || ''}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    processingFee: parseFloat(e.target.value) || 0
                  }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter fee"
                />
                <select
                  value={financialAssessment.processingFeeType}
                  onChange={(e) => setFinancialAssessment(prev => ({
                    ...prev,
                    processingFeeType: e.target.value
                  }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="amount">₹</option>
                  <option value="percentage">%</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Disbursal Mode</label>
              <select
                value={financialAssessment.disbursalMode}
                onChange={(e) => setFinancialAssessment(prev => ({
                  ...prev,
                  disbursalMode: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="direct_hospital">Direct to Hospital</option>
                <option value="digital_wallet">Digital Wallet</option>
              </select>
            </div>
          </div>
        </div>


        {/* Internal Notes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-gray-100 p-2 rounded-lg">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Internal Assessment Notes</h3>
            </div>
          </div>

          <textarea
            value={financialAssessment.internalNotes}
            onChange={(e) => setFinancialAssessment(prev => ({
              ...prev,
              internalNotes: e.target.value
            }))}
            rows="6"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Add detailed notes about the financial assessment, risk factors, special conditions, or any other relevant information..."
          />
        </div>
      </div>
    );
  }



  // Document Verification - Enhanced with Finance Team Verification Checkboxes
  function renderDocumentVerification() {
    const openDocument = (document) => {
      // Open in the right panel, preserve scroll position
      scrollYBeforeDocRef.current = window.scrollY;
      setSelectedDocument(document);
      setShowDocumentPanel(true);
      setTimeout(() => window.scrollTo(0, scrollYBeforeDocRef.current || 0), 0);
    };

    const closeDocumentPanel = () => {
      setShowDocumentPanel(false);
      setSelectedDocument(null);
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Application Documents
          </h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowAllDocuments(!showAllDocuments)}
              className="text-sm text-teal-600 hover:text-teal-800 font-medium flex items-center space-x-2 px-3 py-1 rounded border border-teal-200 bg-teal-50 hover:bg-teal-100 transition-colors"
              title={showAllDocuments ? "Show Only Latest Document" : "Show All Documents"}
            >
              <FileText className="h-4 w-4" />
              <span>{showAllDocuments ? "Show Latest Only" : "Show All"}</span>
            </button>
            <div className="text-sm text-gray-600">
              {Object.keys(application.documents || {}).length} document(s) uploaded
            </div>
          </div>
        </div>

        {application.documents &&
          Object.keys(application.documents).length > 0 ? (
          <div>
            {(() => {
              const documentEntries = Object.entries(application.documents);

              // Sort all docs by uploaded time (desc)
              const sortedDocs = documentEntries.sort((a, b) => {
                const timeA = a[1].uploadedAt?.toDate ? a[1].uploadedAt.toDate().getTime() : (a[1].uploadedAt ? new Date(a[1].uploadedAt).getTime() : 0);
                const timeB = b[1].uploadedAt?.toDate ? b[1].uploadedAt.toDate().getTime() : (b[1].uploadedAt ? new Date(b[1].uploadedAt).getTime() : 0);
                return timeB - timeA;
              });

              if (!showAllDocuments) {
                const [key, doc] = sortedDocs[0] || [];
                if (!key) return null;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div
                      key={key}
                      className={`bg-white border border-teal-500 rounded-lg p-4 hover:shadow-md transition-shadow`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <h4 className="font-medium text-gray-900 text-sm">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
                            <span className="ml-2 text-xs text-teal-600 font-bold">(LATEST)</span>
                          </h4>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button onClick={() => openDocument(doc)} className="text-blue-600 hover:text-blue-800 p-1" title="View Document">
                            <Eye className="h-4 w-4" />
                          </button>
                          <button onClick={() => { const link = document.createElement('a'); link.href = doc.url; link.download = doc.fileName; link.click(); }} className="text-green-600 hover:text-green-800 p-1" title="Download Document">
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p className="truncate">{doc.fileName}</p>
                        <p>Size: {(doc.size / 1024).toFixed(1)} KB</p>
                        <p>Uploaded: {formatDate(doc.uploadedAt)}</p>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`finance-verify-${key}`}
                            checked={financialAssessment.documentStatus[key]?.financeVerified || false}
                            onChange={async (e) => {
                              const isChecked = e.target.checked;
                              setFinancialAssessment(prev => ({
                                ...prev,
                                documentStatus: {
                                  ...prev.documentStatus,
                                  [key]: {
                                    ...prev.documentStatus[key],
                                    financeVerified: isChecked,
                                    financeVerifiedBy: isChecked ? user.uid : null,
                                    financeVerifiedAt: isChecked ? new Date().toISOString() : null
                                  }
                                }
                              }));
                              try {
                                const updatedDocumentStatus = {
                                  ...financialAssessment.documentStatus,
                                  [key]: {
                                    ...financialAssessment.documentStatus[key],
                                    financeVerified: isChecked,
                                    financeVerifiedBy: isChecked ? user.uid : null,
                                    financeVerifiedAt: isChecked ? new Date().toISOString() : null
                                  }
                                };
                                await saveFinancialComments(applicationId, {
                                  documentStatus: updatedDocumentStatus,
                                  assessmentData: { ...financialAssessment, documentStatus: updatedDocumentStatus },
                                  lastUpdatedBy: user.uid,
                                  lastUpdatedAt: new Date().toISOString(),
                                  documentVerificationUpdate: true
                                });
                              } catch (error) {
                                setFinancialAssessment(prev => ({
                                  ...prev,
                                  documentStatus: {
                                    ...prev.documentStatus,
                                    [key]: {
                                      ...prev.documentStatus[key],
                                      financeVerified: !isChecked,
                                      financeVerifiedBy: !isChecked ? user.uid : null,
                                      financeVerifiedAt: !isChecked ? new Date().toISOString() : null
                                    }
                                  }
                                }));
                              }
                            }}
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`finance-verify-${key}`} className="text-sm font-medium text-gray-700">Verified for Finance Team</label>
                        </div>
                        {financialAssessment.documentStatus[key]?.financeVerified && (
                          <div className="mt-1 text-xs text-green-600">✓ Verified by finance team</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              // Build grouped sections (when showing all)
              const groups = { policy: [], patientKyc: [], holderKyc: [], medical: [], investigations: [], others: [] };
              sortedDocs.forEach(([key, doc]) => {
                const k = (key || '').toLowerCase();
                if (k.includes('policy') || k.includes('ecard') || k.includes('card')) groups.policy.push([key, doc]);
                else if (k.includes('patient') && (k.includes('aadhar') || k.includes('adhar') || k.includes('pan') || k.includes('address') || k.includes('kyc'))) groups.patientKyc.push([key, doc]);
                else if ((k.includes('policyholder') || k.includes('holder')) && (k.includes('aadhar') || k.includes('adhar') || k.includes('pan') || k.includes('address') || k.includes('kyc'))) groups.holderKyc.push([key, doc]);
                else if (k.includes('medical') || k.includes('treatment') || k.includes('discharge') || k.includes('prescription')) groups.medical.push([key, doc]);
                else if (k.includes('investigation') || k.includes('report') || k.includes('lab') || k.includes('x-ray') || k.includes('xray')) groups.investigations.push([key, doc]);
                else groups.others.push([key, doc]);
              });

              const sections = [
                { key: 'policy', title: 'Policy Documents' },
                { key: 'patientKyc', title: 'Patient KYC' },
                { key: 'holderKyc', title: 'Policy Holder KYC' },
                { key: 'medical', title: 'Medical Documents' },
                { key: 'investigations', title: 'Investigation Reports' },
                { key: 'others', title: 'Other Documents' }
              ];

              return (
                <div className="space-y-6">
                  {sections.map((section) => {
                    const items = groups[section.key] || [];
                    if (items.length === 0) return null;
                    return (
                      <div key={section.key} className="bg-white border border-gray-200 rounded-xl">
                        <div className="flex items-center justify-between px-4 py-3 border-b">
                          <h3 className="text-sm font-semibold text-gray-800">{section.title}</h3>
                          <span className="text-xs text-gray-500">{items.length}</span>
                        </div>
                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {items.map(([key, doc]) => (
                            <div key={key} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center space-x-2">
                                  <FileText className="h-5 w-5 text-blue-500" />
                                  <h4 className="font-medium text-gray-900 text-sm">{key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}</h4>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <button onClick={() => openDocument(doc)} className="text-blue-600 hover:text-blue-800 p-1" title="View Document"><Eye className="h-4 w-4" /></button>
                                  <button onClick={() => { const link = document.createElement('a'); link.href = doc.url; link.download = doc.fileName; link.click(); }} className="text-green-600 hover:text-green-800 p-1" title="Download Document"><Download className="h-4 w-4" /></button>
                                </div>
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <p className="truncate">{doc.fileName}</p>
                                <p>Size: {(doc.size / 1024).toFixed(1)} KB</p>
                                <p>Uploaded: {formatDate(doc.uploadedAt)}</p>
                              </div>
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="flex items-center space-x-2">
                                  <input type="checkbox" id={`finance-verify-${key}`} checked={financialAssessment.documentStatus[key]?.financeVerified || false} onChange={async (e) => {
                                    const isChecked = e.target.checked;
                                    setFinancialAssessment(prev => ({
                                      ...prev,
                                      documentStatus: {
                                        ...prev.documentStatus,
                                        [key]: {
                                          ...prev.documentStatus[key],
                                          financeVerified: isChecked,
                                          financeVerifiedBy: isChecked ? user.uid : null,
                                          financeVerifiedAt: isChecked ? new Date().toISOString() : null
                                        }
                                      }
                                    }));
                                    try {
                                      const updatedDocumentStatus = {
                                        ...financialAssessment.documentStatus,
                                        [key]: {
                                          ...financialAssessment.documentStatus[key],
                                          financeVerified: isChecked,
                                          financeVerifiedBy: isChecked ? user.uid : null,
                                          financeVerifiedAt: isChecked ? new Date().toISOString() : null
                                        }
                                      };
                                      await saveFinancialComments(applicationId, { documentStatus: updatedDocumentStatus, assessmentData: { ...financialAssessment, documentStatus: updatedDocumentStatus }, lastUpdatedBy: user.uid, lastUpdatedAt: new Date().toISOString(), documentVerificationUpdate: true });
                                    } catch (error) {
                                      setFinancialAssessment(prev => ({
                                        ...prev,
                                        documentStatus: {
                                          ...prev.documentStatus,
                                          [key]: {
                                            ...prev.documentStatus[key],
                                            financeVerified: !isChecked,
                                            financeVerifiedBy: !isChecked ? user.uid : null,
                                            financeVerifiedAt: !isChecked ? new Date().toISOString() : null
                                          }
                                        }
                                      }));
                                    }
                                  }} className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
                                  <label htmlFor={`finance-verify-${key}`} className="text-sm font-medium text-gray-700">Verified for Finance Team</label>
                                </div>
                                {financialAssessment.documentStatus[key]?.financeVerified && (
                                  <div className="mt-1 text-xs text-green-600">✓ Verified by finance team</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Documents
            </h3>
            <p className="text-gray-600">
              No documents have been uploaded for this application.
            </p>
          </div>
        )}

        {/* Document Panel Modal removed: documents open in a new tab */}
      </div>
    );
  }

  // Credit Analysis - Enhanced with Additional Information Options
  function renderCreditAnalysis() {
    const addCustomField = async () => {
      const newFields = [...creditAdditionalInfo.customFields, { label: '', value: '', type: 'text' }];
      setCreditAdditionalInfo(prev => ({
        ...prev,
        customFields: newFields
      }));

      // Auto-save to Firestore
      await autoSaveCreditInfo({
        ...creditAdditionalInfo,
        customFields: newFields
      });
    };

    const updateCustomField = async (index, field, value) => {
      const updatedFields = creditAdditionalInfo.customFields.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      );

      setCreditAdditionalInfo(prev => ({
        ...prev,
        customFields: updatedFields
      }));

      // Auto-save to Firestore with debounce
      clearTimeout(window.creditFieldTimeout);
      window.creditFieldTimeout = setTimeout(async () => {
        await autoSaveCreditInfo({
          ...creditAdditionalInfo,
          customFields: updatedFields
        });
      }, 1000); // 1 second debounce
    };

    const removeCustomField = async (index) => {
      const filteredFields = creditAdditionalInfo.customFields.filter((_, idx) => idx !== index);
      setCreditAdditionalInfo(prev => ({
        ...prev,
        customFields: filteredFields
      }));

      // Auto-save to Firestore
      await autoSaveCreditInfo({
        ...creditAdditionalInfo,
        customFields: filteredFields
      });
    };

    const addNote = async () => {
      const note = prompt('Enter additional note:');
      if (note && note.trim()) {
        const newNote = {
          note: note.trim(),
          addedBy: user.profile?.name || user.email,
          addedAt: new Date().toISOString()
        };

        const updatedNotes = [...creditAdditionalInfo.additionalNotes, newNote];
        setCreditAdditionalInfo(prev => ({
          ...prev,
          additionalNotes: updatedNotes
        }));

        // Auto-save to Firestore
        await autoSaveCreditInfo({
          ...creditAdditionalInfo,
          additionalNotes: updatedNotes
        });
      }
    };

    const removeNote = async (index) => {
      const filteredNotes = creditAdditionalInfo.additionalNotes.filter((_, idx) => idx !== index);
      setCreditAdditionalInfo(prev => ({
        ...prev,
        additionalNotes: filteredNotes
      }));

      // Auto-save to Firestore
      await autoSaveCreditInfo({
        ...creditAdditionalInfo,
        additionalNotes: filteredNotes
      });
    };

    // Auto-save function for credit info
    const autoSaveCreditInfo = async (creditInfo) => {
      try {
        setCreditSaveStatus(prev => ({ ...prev, isSaving: true, error: null }));

        const updatedAssessment = {
          ...financialAssessment,
          creditAdditionalInfo: creditInfo
        };

        // Update financial assessment state
        setFinancialAssessment(updatedAssessment);

        // Save to Firestore
        await saveFinancialComments(applicationId, {
          creditAdditionalInfo: creditInfo,
          assessmentData: updatedAssessment,
          lastUpdatedBy: user.uid,
          lastUpdatedAt: new Date().toISOString(),
          autoSaved: true
        });

        setCreditSaveStatus({
          isSaving: false,
          lastSaved: new Date(),
          error: null
        });

        console.log('Credit info auto-saved to Firestore');
      } catch (error) {
        console.error('Error auto-saving credit info:', error);
        setCreditSaveStatus(prev => ({
          ...prev,
          isSaving: false,
          error: 'Failed to save'
        }));
      }
    };

    // Handle input field changes with auto-save
    const handleCreditFieldChange = async (field, value) => {
      const updatedInfo = {
        ...creditAdditionalInfo,
        [field]: value
      };

      setCreditAdditionalInfo(updatedInfo);

      // Auto-save to Firestore with debounce
      clearTimeout(window.creditFieldTimeout);
      window.creditFieldTimeout = setTimeout(async () => {
        await autoSaveCreditInfo(updatedInfo);
      }, 1000); // 1 second debounce
    };

    // Save additional info to financial assessment and Firestore
    const saveAdditionalInfo = async () => {
      try {
        setCreditSaveStatus(prev => ({ ...prev, isSaving: true, error: null }));

        // Update local state
        setFinancialAssessment(prev => ({
          ...prev,
          creditAdditionalInfo: creditAdditionalInfo
        }));

        // Save to Firestore immediately
        await saveFinancialComments(applicationId, {
          creditAdditionalInfo: creditAdditionalInfo,
          assessmentData: {
            ...financialAssessment,
            creditAdditionalInfo: creditAdditionalInfo
          },
          lastUpdatedBy: user.uid,
          lastUpdatedAt: new Date().toISOString(),
          manualSave: true
        });

        setCreditSaveStatus({
          isSaving: false,
          lastSaved: new Date(),
          error: null
        });

        alert('Credit information saved successfully!');
      } catch (error) {
        console.error('Error saving credit information:', error);
        setCreditSaveStatus(prev => ({
          ...prev,
          isSaving: false,
          error: 'Failed to save'
        }));
        alert('Failed to save credit information');
      }
    };

    return (
      <div className="space-y-6">
        {/* Credit Information Summary - Display all entered data */}
        {(creditAdditionalInfo.customCreditScore || 
          creditAdditionalInfo.previousCreditHistory || 
          creditAdditionalInfo.defaultHistory ||
          creditAdditionalInfo.customFields.length > 0 ||
          creditAdditionalInfo.additionalNotes.length > 0) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Credit Information Summary</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Display entered credit score */}
              {creditAdditionalInfo.customCreditScore && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Manual Credit Score</h4>
                  <div className="text-2xl font-bold text-blue-600">
                    {creditAdditionalInfo.customCreditScore}
                  </div>
                  <p className="text-sm text-blue-700">From {creditAdditionalInfo.creditBureauName}</p>
                  {creditAdditionalInfo.creditReportDate && (
                    <p className="text-sm text-blue-600">
                      Report Date: {new Date(creditAdditionalInfo.creditReportDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
              
              {/* Display credit history */}
              {creditAdditionalInfo.previousCreditHistory && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 mb-2">Credit History</h4>
                  <p className="text-green-700 capitalize">
                    {creditAdditionalInfo.previousCreditHistory.replace('_', ' ')}
                  </p>
                </div>
              )}
              
              {/* Display default history */}
              {creditAdditionalInfo.defaultHistory && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-2">Default History</h4>
                  <p className="text-yellow-700 capitalize">
                    {creditAdditionalInfo.defaultHistory.replace('_', ' ')}
                  </p>
                </div>
              )}
            </div>
            
            {/* Display custom fields */}
            {creditAdditionalInfo.customFields.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-900 mb-4">Additional Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {creditAdditionalInfo.customFields.map((field, index) => (
                    field.label && field.value && (
                      <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <h5 className="font-medium text-gray-700 text-sm">{field.label}</h5>
                        <p className="text-gray-600 mt-1">
                          {field.type === 'date' ? 
                            new Date(field.value).toLocaleDateString() : 
                            field.value}
                        </p>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
            
            {/* Display additional notes */}
            {creditAdditionalInfo.additionalNotes.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-900 mb-4">Additional Notes</h4>
                <div className="space-y-3">
                  {creditAdditionalInfo.additionalNotes.map((noteItem, index) => (
                    <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-gray-900">{noteItem.note}</p>
                      <div className="flex items-center space-x-2 mt-2 text-sm text-gray-500">
                        <User className="h-3 w-3" />
                        <span>{noteItem.addedBy}</span>
                        <span>•</span>
                        <span>{new Date(noteItem.addedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Enhanced Credit Information Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Additional Credit Information</h3>
            <div className="flex items-center space-x-4">
              {/* Auto-save Status */}
              <div className="flex items-center space-x-2">
                {creditSaveStatus.isSaving ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin text-blue-600" />
                    <span className="text-sm text-blue-600">Saving...</span>
                  </>
                ) : creditSaveStatus.lastSaved ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-600">
                      Saved at {creditSaveStatus.lastSaved.toLocaleTimeString()}
                    </span>
                  </>
                ) : creditSaveStatus.error ? (
                  <>
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-red-600">{creditSaveStatus.error}</span>
                  </>
                ) : null}
              </div>

              <button
                onClick={saveAdditionalInfo}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Save className="h-4 w-4" />
                <span>Manual Save</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Custom Credit Score Entry */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Manual Credit Score Entry
              </label>
              <input
                type="number"
                min="300"
                max="900"
                value={creditAdditionalInfo.customCreditScore}
                onChange={(e) => handleCreditFieldChange('customCreditScore', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter credit score manually"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use this if automatic fetch failed or for manual override
              </p>
            </div>

            {/* Credit Bureau Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Credit Bureau
              </label>
              <select
                value={creditAdditionalInfo.creditBureauName}
                onChange={(e) => handleCreditFieldChange('creditBureauName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="CIBIL">CIBIL TransUnion</option>
                <option value="Experian">Experian</option>
                <option value="Equifax">Equifax</option>
                <option value="CRIF">CRIF High Mark</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Credit Report Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Credit Report Date
              </label>
              <input
                type="date"
                value={creditAdditionalInfo.creditReportDate}
                onChange={(e) => handleCreditFieldChange('creditReportDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Previous Credit History */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Previous Credit History
              </label>
              <select
                value={creditAdditionalInfo.previousCreditHistory}
                onChange={(e) => handleCreditFieldChange('previousCreditHistory', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select history</option>
                <option value="excellent">Excellent Payment History</option>
                <option value="good">Good Payment History</option>
                <option value="fair">Fair Payment History</option>
                <option value="poor">Poor Payment History</option>
                <option value="no_history">No Previous Credit History</option>
              </select>
            </div>

            {/* Default History */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default/Settlement History
              </label>
              <select
                value={creditAdditionalInfo.defaultHistory}
                onChange={(e) => handleCreditFieldChange('defaultHistory', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select default history</option>
                <option value="no_defaults">No Defaults/Settlements</option>
                <option value="minor_delays">Minor Payment Delays Only</option>
                <option value="settled_accounts">Has Settled Accounts</option>
                <option value="active_defaults">Active Defaults</option>
                <option value="bankruptcy">Bankruptcy History</option>
              </select>
            </div>
          </div>
        </div>

        {/* Custom Information Fields */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Custom Information Fields</h3>
            <button
              onClick={addCustomField}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Field</span>
            </button>
          </div>

          {creditAdditionalInfo.customFields.length > 0 ? (
            <div className="space-y-4">
              {creditAdditionalInfo.customFields.map((field, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border border-gray-200 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Label
                    </label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateCustomField(index, 'label', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Field name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Type
                    </label>
                    <select
                      value={field.type}
                      onChange={(e) => updateCustomField(index, 'type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="date">Date</option>
                      <option value="select">Select</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Value
                    </label>
                    <input
                      type={field.type}
                      value={field.value}
                      onChange={(e) => updateCustomField(index, 'value', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter value"
                    />
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={() => removeCustomField(index)}
                      className="w-full bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 flex items-center justify-center space-x-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Database className="h-8 w-8 mx-auto mb-2" />
              <p>No custom fields added yet</p>
            </div>
          )}
        </div>

        {/* Additional Notes Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Additional Notes</h3>
            <button
              onClick={addNote}
              className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 flex items-center space-x-2"
            >
              <Edit className="h-4 w-4" />
              <span>Add Note</span>
            </button>
          </div>

          {creditAdditionalInfo.additionalNotes.length > 0 ? (
            <div className="space-y-3">
              {creditAdditionalInfo.additionalNotes.map((noteItem, index) => (
                <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-gray-900">{noteItem.note}</p>
                      <div className="flex items-center space-x-2 mt-2 text-sm text-gray-500">
                        <User className="h-3 w-3" />
                        <span>{noteItem.addedBy}</span>
                        <span>•</span>
                        <span>{new Date(noteItem.addedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeNote(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-8 w-8 mx-auto mb-2" />
              <p>No additional notes added yet</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Discussions
  function renderDiscussions() {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Discussions</h3>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {discussions.length > 0 ? (
            discussions.map((discussion) => (
              <div key={discussion.id} className="border-b border-gray-200 pb-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-gray-100 p-2 rounded-full">
                    <User className="h-4 w-4 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-gray-900">{discussion.senderName}</span>
                      <span className="text-sm text-gray-500">{formatDate(discussion.timestamp)}</span>
                    </div>
                    <p className="text-gray-700">{discussion.message}</p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-600 text-center py-8">No discussions yet</p>
          )}
        </div>

        <div className="mt-4">
          <div className="flex space-x-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSendMessage();
                }
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Type your message..."
            />
            <button
              onClick={handleSendMessage}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Status Timeline
  function renderStatusTimeline() {
    const items = timelineEvents || [];
    const getIconForType = (type) => {
      const mapping = {
        submission: FileText,
        medical_review: Stethoscope,
        policy_review: Shield,
        financial_review: Calculator,
        kyc_verification: User,
        status_change: Activity,
        dispute_raised: AlertTriangle,
        acknowledged: Activity,
        under_review: Activity,
        dispute_resolved: CheckCircle,
        doc_query_open: Paperclip,
        doc_query_resolved: CheckCircle,
      };
      return mapping[type] || Activity;
    };

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Status Timeline</h3>

        <div className="space-y-6">
          {items.length === 0 && (
            <div className="text-center text-sm text-gray-500">No timeline events yet.</div>
          )}
          {items.map((item, index) => {
            const Icon = getIconForType(item.type);
            const isCompleted = !!item.completed;
            return (
              <div key={`${item.id || item.type}-${index}`} className="flex items-start space-x-4">
                <div className={`p-2 rounded-full ${isCompleted ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Icon className={`h-4 w-4 ${isCompleted ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{item.title}</h4>
                  {item.timestamp && (
                    <p className="text-sm text-gray-600">{formatDate(item.timestamp)}</p>
                  )}
                  {item.description && (
                    <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                  )}
                  {item.actor?.name && (
                    <div className="mt-2 text-xs text-gray-600">
                      <span className="px-2 py-0.5 rounded-full bg-gray-100 border text-gray-700">
                        {item.actor.name}{item.actor.team ? ` • ${item.actor.team}` : ''}
                      </span>
                    </div>
                  )}
                  {Array.isArray(item.attachments) && item.attachments.length > 0 && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      {item.attachments.map((att, i) => (
                        <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                          <Paperclip className="h-3 w-3" /> {att.name || 'Attachment'}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Sanctions and Letters rendering
  function renderSanctions() {
    const availableLetters = [
      {
        id: 'kyp',
        title: 'KYP Letter',
        description: 'Know Your Patient verification letter',
        icon: User,
        color: 'blue'
      },
      {
        id: 'approval',
        title: 'Final Approval Letter',
        description: 'Application approval confirmation',
        icon: CheckCircle,
        color: 'green'
      },
      {
        id: 'disbursal',
        title: 'Disbursal Letter',
        description: 'Treatment cost disbursal authorization',
        icon: CreditCard,
        color: 'purple'
      },
      {
        id: 'closure',
        title: 'Case Closure Letter',
        description: 'Final case closure and settlement',
        icon: Award,
        color: 'orange'
      }
    ];

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Official Letters & Documents</h2>
            <p className="text-sm text-gray-600">View and download official letters and documents</p>
          </div>
        </div>

        {/* Available Letters - mirror ApplicationDetails behavior */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {availableLetters.map((letter) => {
            const Icon = letter.icon;
            
            // Handle KYP letter specially since it's stored in workflow
            let existingLetter;
            if (letter.id === 'kyp' && application.workflow?.medicalVerification?.kypGenerated) {
              existingLetter = {
                id: 'kyp',
                type: 'kyp',
                title: 'KYP Letter',
                url: null, // KYP uses dynamic PDF generation
                fileName: 'KYP-Letter.pdf',
                generatedAt: application.workflow.medicalVerification.kypGeneratedAt || application.workflow.medicalVerification.verifiedAt || new Date(),
                generatedBy: 'Medical Team',
                isKYPLetter: true,
                kypData: application.workflow.medicalVerification.kypData,
                status: application.workflow?.medicalVerification?.doctorApproval?.status || 'pending'
              };
            } else {
              existingLetter = sanctions.find(s => s.type === letter.id);
            }

            // Fallback for approval letter if workflow has quick link
            if (!existingLetter && letter.id === 'approval' && application.workflow?.dischargeSettlement?.finalApprovalLetterUrl) {
              existingLetter = {
                id: 'approval-quicklink',
                type: 'approval',
                title: 'Final Approval Letter',
                url: application.workflow.dischargeSettlement.finalApprovalLetterUrl,
                fileName: 'Final-Approval-Letter.pdf',
                generatedAt: application.workflow?.dischargeSettlement?.processedAt || application.updatedAt || new Date(),
                generatedBy: 'Financial Team',
                status: 'approved'
              };
            }
            // Availability gating aligned with ApplicationDetails
            const isAvailableByStatus = (
              (letter.id === 'kyp' && application.workflow?.medicalVerification?.status === 'approved') ||
              (letter.id === 'approval' && application.status === 'approved') ||
              (letter.id === 'disbursal' && application.status === 'approved' && application.disbursalLetterGenerated) ||
              (letter.id === 'closure' && application.status === 'closed')
            );
            const isAvailable = Boolean(existingLetter) || isAvailableByStatus;
            const status = existingLetter?.status || existingLetter?.hospitalAcknowledgement?.status || 'pending';

            return (
              <div key={letter.id} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      isAvailable ? `bg-${letter.color}-100` : 'bg-gray-100'
                    }`}>
                      <Icon className={`h-5 w-5 ${
                        isAvailable ? `text-${letter.color}-600` : 'text-gray-400'
                      }`} />
                    </div>
                    <div>
                      <h3 className={`font-medium ${isAvailable ? 'text-gray-900' : 'text-gray-500'}`}>
                        {letter.title}
                      </h3>
                      <p className={`text-sm ${isAvailable ? 'text-gray-600' : 'text-gray-400'}`}>
                        {letter.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      isAvailable 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isAvailable ? 'Available' : 'Pending'}
                    </span>
                    {isAvailable && (
                      <div className="mt-1">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          status === 'accepted' || status === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : status === 'challenged' || status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {status === 'accepted' || status === 'approved' ? 'Accepted' :
                           status === 'challenged' || status === 'rejected' ? 'Challenged' : 'Pending Review'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {existingLetter ? (
                  <div className="space-y-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-800">Letter Available</p>
                          <p className="text-xs text-green-600">
                            Generated on {formatDate(existingLetter.generatedAt)}
                          </p>
                          <p className="text-xs text-green-600">
                            By {existingLetter.generatedBy}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openLetter(existingLetter)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                            title="View Letter"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleLetterDownload(existingLetter)}
                            className="px-3 py-1 rounded text-sm flex items-center space-x-1 bg-blue-600 text-white hover:bg-blue-700"
                          >
                            <Download className="h-3 w-3" />
                            <span>Download</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : isAvailableByStatus ? (
                  <button
                    onClick={() => generateAndDownloadLetter(letter.id)}
                    disabled={actionLoading[letter.id]}
                    className="w-full bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {actionLoading[letter.id] ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        <span>Generate & Download</span>
                      </>
                    )}
                  </button>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500">
                      {letter.id === 'kyp' ? 'Complete medical review to generate' :
                       letter.id === 'approval' ? 'Complete all verifications to generate' :
                       letter.id === 'disbursal' ? 'Application approval required' :
                       'Case closure required'}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Generated Documents History */}
        {sanctions.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Document History</h3>
            
            <div className="space-y-3">
              {sanctions.map((sanction) => (
                <div key={sanction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-gray-600" />
                    <div>
                      <p className="font-medium text-gray-900">{sanction.title}</p>
                      <p className="text-sm text-gray-600">
                        Generated by {sanction.generatedBy} on {formatDate(sanction.generatedAt)}
                      </p>
                      {sanction.hospitalAcknowledgement && (
                        <p className="text-xs mt-1">
                          Status: <span className={`${
                            sanction.hospitalAcknowledgement.status === 'accepted' ? 'text-green-700' : 
                            sanction.hospitalAcknowledgement.status === 'challenged' ? 'text-red-700' : 'text-yellow-700'
                          }`}>
                            {(sanction.hospitalAcknowledgement.status || 'pending').toUpperCase()}
                          </span>
                          {sanction.hospitalAcknowledgement.comment ? ` • ${sanction.hospitalAcknowledgement.comment}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openLetter(sanction)}
                      className="text-blue-600 hover:text-blue-800"
                      title="View Letter"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleLetterDownload(sanction)}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center space-x-1"
                    >
                      <Download className="h-3 w-3" />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Enhanced Sidebar matching Policy Review design
  function renderSidebar() {
    return (
      <div className="space-y-6">
        {/* Financial Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h3>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Requested Amount</span>
              <span className="font-medium text-gray-900">
                {formatAmount(application.medical?.estimatedCost || application.requestedLoanAmount || 0)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Eligible Amount</span>
              <span className="font-medium text-blue-600">
                {formatAmount(financialAssessment.eligibleAmount)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Recommended Amount</span>
              <span className="font-medium text-green-600">
                {formatAmount(financialAssessment.recommendedAmount)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Approved Amount</span>
              <span className="font-medium text-purple-600">
                {formatAmount(financialAssessment.approvedAmount)}
              </span>
            </div>

            <hr className="my-3" />

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Interest Rate</span>
              <span className="font-medium text-gray-900">
                {financialAssessment.interestRate || 0}%
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tenure</span>
              <span className="font-medium text-gray-900">
                {financialAssessment.tenure || 12} months
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">EMI</span>
              <span className="font-medium text-gray-900">
                {formatAmount(financialAssessment.emi)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">FOIR</span>
              <span className={`font-medium ${financialAssessment.foir <= 50 ? 'text-green-600' :
                  financialAssessment.foir <= 70 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                {financialAssessment.foir || 0}%
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Risk Category</span>
              <span className={`font-medium capitalize ${financialAssessment.riskCategory === 'low' ? 'text-green-600' :
                  financialAssessment.riskCategory === 'medium' ? 'text-yellow-600' : 'text-red-600'
                }`}>
                {financialAssessment.riskCategory || 'Not assessed'}
              </span>
            </div>

            {financialAssessment.loanId && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Loan ID</span>
                <span className="font-medium text-gray-900 font-mono text-xs">
                  {financialAssessment.loanId}
                </span>
              </div>
            )}

            {financialAssessment.lenderName && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Lender</span>
                <span className="font-medium text-gray-900 text-xs">
                  {financialAssessment.lenderName}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Credit & Verification Status */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Status</h3>

          <div className="space-y-3">
            {[
              { key: 'creditScore', label: 'Credit Score', value: financialAssessment.creditScore, type: 'score' },
              { key: 'aadharVerified', label: 'Aadhar Verified', value: financialAssessment.aadharVerified, type: 'boolean' },
              { key: 'panVerified', label: 'PAN Verified', value: financialAssessment.panVerified, type: 'boolean' },
              { key: 'bankAccountVerified', label: 'Bank Account', value: financialAssessment.bankAccountVerified, type: 'boolean' },
              { key: 'incomeVerified', label: 'Income Verified', value: financialAssessment.incomeVerified, type: 'boolean' },
              { key: 'employmentVerified', label: 'Employment', value: financialAssessment.employmentVerified, type: 'boolean' },
              { key: 'documentsAuthentic', label: 'Documents', value: financialAssessment.documentsAuthentic, type: 'boolean' }
            ].map((item) => (
              <div key={item.key} className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{item.label}</span>
                {item.type === 'score' ? (
                  <span className={`font-medium ${item.value >= 750 ? 'text-green-600' :
                      item.value >= 650 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                    {item.value || 'Not checked'}
                  </span>
                ) : (
                  <div className="flex items-center space-x-1">
                    {item.value ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                    <span className={`text-xs font-medium ${item.value ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {item.value ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Internal Comments */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Internal Comments</h3>

          {allInternalComments && allInternalComments.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Previous Comments from Financial Team:</h4>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {allInternalComments.map((comment, index) => (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-blue-600">
                        {comment.userName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {comment.timestamp?.toDate?.()?.toLocaleString() ||
                          new Date(comment.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.comments}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Comments</label>
            <textarea
              value={internalComments}
              onChange={(e) => setInternalComments(e.target.value)}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              placeholder="Add your financial assessment notes..."
            />
          </div>

          {savedInternalComments && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm font-medium text-green-800 mb-1">Your Last Saved Comments:</p>
              <p className="text-sm text-green-700 whitespace-pre-wrap">{savedInternalComments}</p>
            </div>
          )}

          <button
            onClick={handleSaveInternalComments}
            disabled={commentsSaving || !internalComments.trim()}
            className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {commentsSaving ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Save Comments</span>
              </>
            )}
          </button>
        </div>

        {/* Review Decision */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Decision</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Decision</label>
              <select
                value={reviewDecision}
                onChange={(e) => setReviewDecision(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Decision</option>
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
                <option value="pending_info">Request More Information</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Comments *</label>
              <textarea
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="Provide detailed comments about your decision..."
              />
            </div>

            <button
              onClick={handleCompleteReview}
              disabled={
                !reviewDecision ||
                !reviewComments.trim() ||
                actionLoading.complete ||
                disputeStatus.hasActiveDisputes
              }
              className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {actionLoading.complete ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Completing...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Complete Review</span>
                </>
              )}
            </button>

            {disputeStatus.hasActiveDisputes && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">
                    Active disputes must be resolved first
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Document Query Modal */}
        {showQueryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Raise Document Query</h3>
                    <p className="text-sm text-gray-600">
                      {queryDocumentKey?.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowQueryModal(false);
                    setQueryComment("");
                    setQueryDocumentKey("");
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(80vh-180px)]">
              {/* Previous queries */}
              {documentQueries[queryDocumentKey] && documentQueries[queryDocumentKey].length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Previous Queries</h4>
                  {documentQueries[queryDocumentKey].map((query) => (
                    <div key={query.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900">{query.raisedBy?.name}</span>
                            <span className="text-xs text-gray-500">({query.raisedBy?.team})</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              query.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {query.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-1">{query.comment}</p>
                        </div>
                      </div>
                      {query.replies && query.replies.length > 0 && (
                        <div className="mt-2 pl-4 border-l-2 border-gray-200 space-y-2">
                          {query.replies.map((reply, idx) => (
                            <div key={idx} className="text-sm">
                              <span className="font-medium text-gray-900">{reply.userName}:</span>{" "}
                              <span className="text-gray-700">{reply.message}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* New query input */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">New Query</h4>
                <textarea
                  value={queryComment}
                  onChange={(e) => setQueryComment(e.target.value)}
                  placeholder="Describe the issue with this document..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowQueryModal(false);
                  setQueryComment("");
                  setQueryDocumentKey("");
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRaiseQuery}
                disabled={!queryComment.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Raise Query
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    );
  }

  // Helper function to send messages
  async function handleSendMessage() {
    if (!newMessage.trim()) return;

    try {
      const messageData = {
        message: newMessage,
        sender: user?.uid,
        senderName: user.profile?.name || user.email,
        senderRole: "financial",
        messageType: "text",
        timestamp: new Date(),
        readBy: [user?.uid]
      };

      await sendDiscussionMessage(applicationId, messageData);
      setNewMessage("");
      loadDiscussions();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  }

  return null; // This should never be reached due to the render functions above
};

export default FinancialApplicationReview;
